"use client";

import { useState, useCallback, useEffect, useMemo, useRef, type ReactNode } from "react";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { StudioPianoHintBridge } from "@/components/studio/studio-piano-hint-bridge";
import { PianoViewport2 } from "@/components/piano-viewport/piano-viewport2";
import { Toolbar } from "@/components/ui/organisms/toolbar";
import { type LoopRange } from "@/components/ui/organisms/section-navigator";
import { UploadModal } from "@/components/ui/organisms/upload-modal";
import { useAccountModal } from "@/contexts/account-modal-context";
import { GeneratePanel } from "@/components/ui/organisms/generate-panel";
import { LiveSessionPanel } from "@/components/live/live-session-panel";
import { ReplayCamera } from "@/components/live/replay-camera";
import { ReplayNotice } from "@/components/live/replay-notice";
import { PracticeModal } from "@/components/ui/organisms/practice-modal";
import {
  ChordsOverlay,
  ChordsProvider,
  ChordsSidebar,
} from "@/components/ui/organisms/chords-overlay";
import {
  HarmonyOverlay,
  HarmonySidebar,
  type HarmonyFocus,
} from "@/components/ui/organisms/harmony";
import {
  PieceOverviewOverlay,
  type PieceOverviewPhase,
} from "@/components/ui/organisms/piece-overview";
import { SettingsOverlay } from "@/components/studio/settings-overlay";
import { StudioCountIn } from "@/components/studio/count-in";
import { StudioSectionTips } from "@/components/studio/section-tips";
import { LADDER_SHAPE, asAnalysis, fetchRung } from "@/lib/levels/client";
import type { HarmonyContext } from "@/lib/harmony/hud";
import { keySpansOf } from "@/lib/harmony/key-spans";
import { withoutUnplayedExtension } from "@/lib/harmony/triads";
import { HarmonyLevel } from "@/components/learn-piece/harmony/harmony-level";
import { LevelLabSidebar } from "@/components/levels/level-lab-sidebar";

import {
  useStudio,
  useStudioEngagement,
  useStudioView,
  useStudioSettingsOpen,
  useMinimapWidth,
  usePianoHeight,
} from "@/contexts/studio-context";

import { whenAudioRunning } from "@/lib/audio/audio-running";
import { SongControler } from "@/lib/track/controler/song-controler";
import { createSongFromAnalysis, createSongFromRawAnalysis } from "@/lib/music/song-factory";
import type {
  AnalysisResult,
  AnalysisRun,
  AnalysisSection,
  ApiSongResponse,
  QualityFlag,
} from "@/lib/music/analyzer-types";
import {
  ApiError,
  loadPieceSong,
  transposeMidi,
  type ExerciseDetail,
} from "@/lib/api/api-client";
import { consumePieceSource, track } from "@/lib/analytics";
import type {
  PieceLoadFailureReason,
  PieceSource,
  StudioContextType,
} from "@/lib/analytics";
import { recordLoopArmed } from "@/lib/analytics/studio-session";
import { resolveOkWithHintsToastMessage } from "@/lib/upload/ok-with-hints-wording";
import { isMuseScoreFile } from "@/lib/upload/score-file";
import { trackOkWithHintsToastShown } from "@/lib/telemetry/midi-quality-events";
import {
  endExerciseSessionUnlessOwnedBy,
  startExerciseSession,
} from "@/lib/analytics/exercise-session";
import { ToastBanner } from "@/components/ui";
import {
  shortestSemitones,
  tonalityFromDetectedKey,
  type Tonality,
} from "@/lib/music/tonality";
import { StudioFrame } from "./studio/studio-frame";
import { useStudioCanvasCover } from "@/hooks/use-studio-canvas-cover";
import { ALL_SURFACES, type StudioSurface } from "@/lib/studio/canvas-cover";
import { LearnPiecePage } from "@/components/learn-piece/learn-piece-page";
import { RecitalCamera } from "@/components/live/recital-camera";
import { RecitalWidgets } from "@/components/live/recital-widgets";
import { JamWidgets } from "@/components/live/jam-widget";
import { LobbyWidget } from "@/components/live/lobby";
import { ReplayTakesWidget } from "@/components/live/replay-takes-widget";
import { ChatWidgetDemo } from "@/components/roll-widgets/chat-widget-demo";
import { ReactionWidgetDemo } from "@/components/roll-widgets/reaction-widget-demo";
import { VideoWidgetDemo } from "@/components/roll-widgets/video-widget-demo";
import {
  LIBRARY_BASE_PATH,
  LIBRARY_MINE_PATH,
  LIBRARY_PLAYLISTS_PATH,
  LIBRARY_RESUME_PATH,
  LIBRARY_SHARE_PATH,
  LIBRARY_SHARED_PATH,
} from "@/lib/library/library-routes";
import {
  isStudioPathname,
  studioViewFromPathname,
  studioViewPath,
} from "@/lib/studio/studio-routes";
import { useLiveSession } from "@/contexts/live-session-context";
import { useReplay } from "@/contexts/replay-context";
import { DefaultStudioSidebar } from "./studio/default-studio-sidebar";
import {
  EmptyStudioOverlay,
  type ResumeOffer,
} from "./studio/empty-studio-overlay";
import {
  repertoire,
  type Offer,
} from "@/lib/learning-journey/repertoire";
import {
  journeyGlanceLabel,
  readJourneyGlance,
} from "@/lib/library/journey-glance";
import { actDuration } from "@/components/learn-piece/piece";
import { StudioDropOverlay, type DropErrorKind } from "./studio/studio-drop-overlay";
import { EmptyOverlayRecallButton } from "./studio/empty-overlay-recall-button";
import {
  WindowFileDropZone,
  type DroppedFileKind,
} from "./studio/window-file-drop-zone";
import { WelcomeStudioCard } from "./studio/welcome-studio-card";
import { getJingle, computeJingleDurationMs } from "@/lib/jingle/jingle-service";
import { useBreakpoint } from "@/hooks/use-breakpoint";
import { useCoarsePointer } from "@/hooks/use-coarse-pointer";
import { useObservable } from "@/hooks/use-observable";
import { useStudioEntryFunnel } from "@/hooks/use-studio-entry-funnel";
import { useStudioSession } from "@/hooks/use-studio-session";
import { useAuth } from "@/contexts/auth-context";
import type { ReadonlyObservable } from "@/lib/studio/observable";

const SIDEBAR_PREF_KEY = "studio:sidebar:open";

/**
 * « Aucun morceau », tant que le studio n'existe pas : ce que la page lit à
 * la place de `sequence.current` avant que le studio soit construit. Une
 * constante de module, pour que `useObservable` garde le même abonnement d'un
 * rendu à l'autre.
 */
const NO_SONG: ReadonlyObservable<SongControler | null> = {
  value: null,
  subscribe: () => () => {},
};

/**
 * localStorage flag that, when set to `"1"`, skips the welcome card (and
 * therefore the jingle) at boot. By default the card shows on every reload
 * so the jingle plays again, the user opts out from the Settings overlay.
 */
const WELCOME_DISABLED_LS_KEY = "virtuosa.welcomeDisabled";

/**
 * Boot phase before the studio reaches its idle state.
 *
 *   "checking" : SSR-safe placeholder, resolved on the client's first effect.
 *   "welcome"  : the welcome card is up.
 *   "jingle"   : the jingle is playing in the piano-roll.
 *   "ready"    : normal studio (empty-state overlay or loaded piece).
 */
type BootPhase = "checking" | "welcome" | "jingle" | "ready";

/** Buffer added to the jingle scheduled duration before cleanup fires. */
const JINGLE_END_BUFFER_MS = 300;
/** Delay before arming the skip listener, prevents the welcome-card click from instantly skipping. */
const JINGLE_SKIP_ARM_DELAY_MS = 300;
/** Breathing room between "Entrer dans le studio" and the first audible
 *  jingle note. Implemented as a negative pre-roll on the transport so the
 *  notes are visible from the start while audio waits for the playhead
 *  to reach tick 0. */
/**
 * Traces du préchargement du jingle, en développement seulement : en
 * production elles ne feraient que remplir la console de chaque visiteur.
 */
function logJinglePreload(...args: unknown[]): void {
  if (process.env.NODE_ENV !== "production") {
    console.log("[jingle preload]", ...args);
  }
}

const JINGLE_START_DELAY_MS = 700;

/**
 * Ce qu'on accorde au contexte audio pour se réveiller avant de lancer le
 * jingle, en millisecondes.
 *
 * Un contexte sain repart en une image ; celui d'un iPhone qui revient
 * d'arrière-plan met parfois plus longtemps, et tout ce qui se programme
 * avant qu'il rende part au silence. Une seconde et demie est longue pour un
 * réveil et courte pour une attente : au-delà, mieux vaut un jingle muet
 * qu'un écran qui ne s'ouvre pas. Voir `whenAudioRunning`.
 */
const AUDIO_WAKE_TIMEOUT_MS = 1500;

/**
 * Ce que chaque forme de recouvrement cache. Hors du composant : ce sont des
 * constantes, et les recréer à chaque rendu ferait redéclarer pour rien.
 */
const NO_COVERS: readonly StudioSurface[] = [];
const ROLL_COVERS: readonly StudioSurface[] = ["roll"];
const HARMONY_COVERS: readonly StudioSurface[] = ["roll", "piano"];

/**
 * Empty-state overlay timing constants.
 *
 * - `GRACE_MS` : delay between the first note pressed and the actual fade-out.
 *   Anti-frustration buffer for users who tap a key just to test the keyboard.
 * - `REAPPEAR_DEFAULT_MS` : reappearance delay when the user hasn't engaged
 *   yet (e.g. clicked "Joue" but didn't actually play notes).
 * - `REAPPEAR_LIGHT_MS` : longer breathing room when the user has only
 *   played briefly. Avoids cutting through ballads / slow improv.
 * - `ENGAGED_THRESHOLD_MS` : past this much *active* play, we stop bringing
 *   the card back automatically, the user clearly knows what they're
 *   doing. The recall pill stays available for opt-in.
 * - `LIGHT_THRESHOLD_MS` : separates "no activity" / "light" / "engaged".
 */
const GRACE_MS = 1500;
const REAPPEAR_DEFAULT_MS = 8000;
const REAPPEAR_LIGHT_MS = 15000;
const ENGAGED_THRESHOLD_MS = 30000;
const LIGHT_THRESHOLD_MS = 10000;

/**
 * D'où vient la demande d'ouverture, lue sur la route au moment du geste.
 *
 * `loadPiece` est exposé par le contexte du studio et appelé depuis quatre
 * surfaces — une fiche du catalogue, « Mes morceaux », l'atterrissage d'un
 * lien de partage, la récompense d'un programme — dont aucune ne dit d'où elle
 * parle : la signature partagée ne porte que l'identifiant du morceau. La
 * route, elle, le sait, et c'est la même lecture que fait déjà
 * `exercise-session` pour retrouver le programme d'un exercice.
 *
 * C'est un repli, pas une vérité : un appelant qui connaît sa provenance la
 * passe en second argument de `loadPieceFromCatalog` — la reprise du studio
 * vide, ci-dessous, est le seul à le faire aujourd'hui.
 */
function pieceSourceFromPathname(pathname: string): PieceSource {
  // Le lien de partage lui-même, et l'onglet des morceaux qui en sont venus.
  // Les compter sous `library` attribuait au produit une acquisition qui
  // venait de quelqu'un d'autre.
  if (
    pathname.startsWith(`${LIBRARY_SHARE_PATH}/`) ||
    pathname.startsWith(LIBRARY_SHARED_PATH)
  ) {
    return "share";
  }

  if (pathname.startsWith("/learn/")) {
    return "program";
  }

  // Ce que la personne possède déjà : ses morceaux, sa file de reprise, ses
  // playlists. Le reste de `/library` est le catalogue, fiches comprises.
  if (
    pathname.startsWith(LIBRARY_MINE_PATH) ||
    pathname.startsWith(LIBRARY_RESUME_PATH) ||
    pathname.startsWith(LIBRARY_PLAYLISTS_PATH)
  ) {
    return "library";
  }

  if (pathname.startsWith(LIBRARY_BASE_PATH)) {
    return "catalog";
  }

  return "library";
}

/**
 * Pourquoi un morceau n'a pas chargé, en vocabulaire fermé.
 *
 * Le message d'erreur ne part jamais : il vient du serveur, il est rédigé, et
 * il transporte au besoin une URL signée ou un nom de fichier. Ce qui est
 * gardé, c'est la décision qu'il permet de prendre.
 *
 * `fetched` sépare les deux moitiés du chargement : avant, c'est la réponse de
 * l'API ; après, c'est la construction du morceau depuis l'analyse, donc une
 * donnée qu'on n'a pas su lire.
 */
function pieceLoadFailureReason(
  error: unknown,
  fetched: boolean,
): PieceLoadFailureReason {
  if (error instanceof ApiError) {
    if (error.status === 404) {
      return "not_found";
    }

    if (error.status === 401 || error.status === 403) {
      return "forbidden";
    }

    return error.status >= 500 ? "server" : "unknown";
  }

  // `fetch` ne rejette que sur le réseau, et il le fait par un `TypeError` :
  // vrai de l'appel à l'API comme du téléchargement des échantillons, qui
  // tombe de l'autre côté de `fetched`.
  if (error instanceof TypeError) {
    return "network";
  }

  return fetched ? "parse_failed" : "unknown";
}

/**
 * Ce sur quoi porte la session du studio, ou `null` quand le studio n'est pas
 * l'écran.
 *
 * Trois faits suffisent : la page est-elle sous les yeux et démarrée, un
 * morceau est-il chargé, et est-ce un exercice. Le jeu libre est le cas par
 * défaut et non un oubli : c'est l'une des trois portes d'entrée du produit,
 * et la seule qui n'avait jusqu'ici aucune enveloppe de session.
 */
function studioSessionContextOf(
  ready: boolean,
  hasSong: boolean,
  generated: boolean,
): StudioContextType | null {
  if (!ready) {
    return null;
  }

  if (!hasSong) {
    return "free_play";
  }

  return generated ? "exercise" : "piece";
}

export function StudioPage({
  noteDust = false,
}: {
  /** Les notes partent en paillettes en passant la tête de lecture : décidé côté serveur (`NOTE_DUST`). */
  noteDust?: boolean;
} = {}) {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  // StudioPage owns the studio canvas and is only visible on the studio
  // routes (`/studio`, `/studio/harmony`, …). The piece tutorial is a
  // Studio *state* (learn mode) living on those same routes, so no extra
  // path needs to keep it visible.
  const visible = isStudioPathname(pathname);
  const { studio, registerLoadExercise, registerLoadPiece } = useStudio();

  // Réglage de déploiement, lu par le serveur : le studio le descend au roll.
  useEffect(() => {
    studio?.setNoteDust(noteDust);
  }, [studio, noteDust]);
  const [engagement] = useStudioEngagement();
  const [studioView, setStudioView] = useStudioView();
  // Live keyboard height: keep the learn-mode overlay clamped to the top of
  // the keys, matching the Pixi keyboard strip.
  const pianoHeight = usePianoHeight();
  // Largeur vivante du rail de minimap : il se resserre sur un canvas étroit.
  const minimapWidth = useMinimapWidth();

  // Focus partagé entre la sidebar (atlas) et l'overlay (overview / détail).
  // État local : 2 consommateurs voisins, pas besoin de contexte. Volontairement
  // non synchronisé avec l'URL, focus éphémère à ce stade.
  const [harmonyFocus, setHarmonyFocus] = useState<HarmonyFocus>({
    kind: "overview",
  });

  // La route porte la vue (`/studio` = roll, `/studio/harmony`, …), c'est
  // la source de vérité : chaque onglet est partageable et le retour
  // navigateur fonctionne. On la recopie dans l'observable, qui reste ce
  // que lisent le rendu et le runtime. `null` = on n'est pas sur le studio,
  // la vue courante est laissée intacte. La sync vit ici (et pas dans le
  // provider) parce que c'est cette page qui monte les vues.
  const routeView = studioViewFromPathname(pathname);

  useEffect(() => {
    if (!studio || routeView === null) {
      return;
    }

    // Un parcours recouvre le canvas : atterrir sur une vue par l'URL (lien
    // direct, retour navigateur) en sort, sinon l'onglet s'allume et le rendu
    // reste vide. La règle se lit maintenant telle qu'elle est : choisir un
    // instrument d'inspection, c'est quitter l'engagement. Même règle que le
    // clic sur un onglet du header.
    //
    // L'engagement est lu dans l'effet et pas mis en dépendance : entrer dans
    // un parcours depuis une vue pousse `/studio` puis pose l'engagement, et
    // ré-exécuter l'effet sur ce changement l'annulerait aussitôt.
    if (routeView !== "roll" && studio.engagement.value !== null) {
      studio.setEngagement(null);
    }

    studio.setView(routeView);
  }, [studio, routeView]);

  // Compat : les liens `/studio?practice=…` partagés avant que Chords ait
  // sa route dédiée atterriraient sur le piano-roll. On les normalise vers
  // `/studio/chords?practice=…` pour que l'overlay prenne la main.
  const practiceParam = searchParams?.get("practice") ?? null;

  // Banc des widgets du roll, en dev seulement, tant que rien ne les alimente :
  // `/studio?widget=video`, `video-live`, `chat`, `chat-mod` (vu de
  // l'interprète), `hearts`, ou plusieurs séparés par des virgules
  // (`/studio?widget=video-live,chat,hearts`).
  const demoWidgets =
    process.env.NODE_ENV !== "production"
      ? (searchParams?.get("widget") ?? "").split(",")
      : [];

  useEffect(() => {
    if (routeView !== "roll" || practiceParam === null) {
      return;
    }

    router.replace(
      `${studioViewPath("chords")}?practice=${encodeURIComponent(practiceParam)}`,
    );
  }, [router, routeView, practiceParam]);

  // Armé au chargement d'un exercice, consommé dès que le studio est à
  // l'écran : cf. l'effet qui suit `loadExerciseAsSong`.
  const [pendingExerciseStart, setPendingExerciseStart] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [practiceOpen, setPracticeOpen] = useState(false);
  /**
   * Ce qui occupe le bandeau de droite, ou rien.
   *
   * Un seul état pour deux panneaux, et non deux booléens : ils partagent un
   * logement de 380 px et ne peuvent pas y tenir ensemble. Deux booléens
   * auraient demandé de fermer l'autre à chaque ouverture — quatre endroits à
   * tenir en phase pour une exclusion qui est ici structurelle.
   */
  const [rightPanel, setRightPanel] = useState<"generate" | "live" | null>(null);
  const generateOpen = rightPanel === "generate";
  const livePanelOpen = rightPanel === "live";

  // En dessous d'`expanded`, les 200 px de la sidebar laissent trop peu de
  // place au piano-roll : elle se replie par défaut et s'ouvre en tiroir. Même
  // frontière pour le panneau droit, qui recouvre le roll au lieu de le
  // rétrécir — d'où le nom au pluriel.
  const breakpoint = useBreakpoint();
  // La largeur ne suffit pas à décider : un iPad en paysage fait 1180 px et
  // tombe dans `expanded`, au milieu de la plage des portables. Il recevait
  // donc la sidebar ouverte en flux, soit 17 % de la largeur du roll cédés à
  // un rail vide tant qu'aucun morceau n'est chargé — sur l'appareil où le
  // roll est lu à distance de pupitre et a le plus besoin de cette largeur.
  const coarsePointer = useCoarsePointer();

  /**
   * Les deux faits que le premier rendu ne peut pas connaître, résolus
   * ensemble au montage.
   *
   * - `sidebarPref` : préférence de repli de la sidebar. `null` tant que
   *   l'utilisateur ne l'a jamais basculée, c'est alors la règle responsive
   *   qui tranche ; le premier basculement manuel épingle le choix et le
   *   persiste.
   * - `mounted` : `useBreakpoint` annonce `compact` au premier rendu et ne se
   *   corrige qu'à l'effet de montage. Sans cette garde, un poste desktop
   *   peindrait une frame sidebar repliée avant de la rouvrir ; on suppose
   *   donc l'écran large tant que le palier n'est pas mesuré, c'est l'erreur
   *   qui ne se voit pas.
   *
   * Un seul état parce qu'ils arrivent au même instant et décident ensemble
   * de la même chose : deux effets feraient deux rendus en cascade pour une
   * seule information.
   */
  const [resolvedDefaults, setResolvedDefaults] = useState<{
    sidebarPref: boolean | null;
    mounted: boolean;
  }>({ sidebarPref: null, mounted: false });

  useEffect(() => {
    const stored = window.localStorage.getItem(SIDEBAR_PREF_KEY);

    setResolvedDefaults({
      sidebarPref:
        stored === "true" || stored === "false" ? stored === "true" : null,
      mounted: true,
    });
  }, []);

  // Au doigt, les panneaux passent en tiroir quelle que soit la largeur : la
  // garde `mounted` couvre déjà le `false` du premier rendu des deux hooks de
  // media query, donc aucune frame desktop n'est peinte de travers.
  const panelsCompact =
    resolvedDefaults.mounted && (breakpoint !== "expanded" || coarsePointer);

  /**
   * Une séance est en cours : le studio n'est plus jamais « vide ».
   *
   * Trois comportements pensés pour quelqu'un qui joue seul deviennent faux à
   * plusieurs — l'écran d'accueil qui revient, la pause pour inactivité, et la
   * vue locale qu'on laisse décrocher. Aucun des trois ne regarde ce que font
   * les autres, et ils partent tous du même constat : ici, ne rien jouer ne
   * veut pas dire ne rien faire. On écoute son partenaire.
   */
  const live = useLiveSession();
  const replay = useReplay();
  const onAir = live !== null && (live.status === "live" || live.status === "reconnecting");

  /**
   * En récital, la barre latérale part repliée : l'interprète joue devant un
   * public, et le rouleau est ce qu'il partage. Ce choix ne touche pas la
   * préférence retenue — ouvrir la barre pendant le récital vaut pour ce
   * récital, et on la retrouve telle qu'on l'avait laissée en le quittant.
   */
  const recitalId = live?.session?.recital?.id ?? null;

  /**
   * Les lignes de mesure d'un récital appartiennent à la **séance**, pas à cet
   * écran : l'interprète les masque depuis son panneau, le service le publie,
   * et chacun — lui comme le public — suit ce que la métadonnée dit. C'est ce
   * qui fait qu'un geste d'interprète change ce que le public voit, et qu'un
   * spectateur arrivé en cours de récital le trouve déjà posé.
   *
   * Le replay suit ce que la séance disait pendant la prise : masquées au
   * direct, masquées au partage.
   *
   * Hors récital, la trame revient : c'était un choix pour ce récital.
   */
  const replaying = replay.recitalId !== null;
  /**
   * Un replay se prépare sous la page d'une prise, hors du studio : le studio
   * est rendu, à sa taille, mais recouvert par la page et hors d'atteinte
   * (`inert`), le temps qu'on clique « Regarder ».
   */
  const behind = !visible && replaying;
  /**
   * Le canvas Pixi ne démarre qu'à la première venue sur le studio, et ne
   * s'arrête plus ensuite.
   *
   * `StudioPage` est monté en permanence par le layout `(main)`, donc sur
   * **chaque** page du produit : bibliothèque, fiches publiques, réglages. Il
   * n'y était que masqué en CSS (voir le `hidden` du conteneur, tout en bas),
   * mais le canvas, lui, était bel et bien créé. Chaque fiche indexée par un
   * moteur de recherche ouvrait donc un contexte WebGL, une scène complète et
   * une boucle de rendu qu'elle n'affichait jamais — jusqu'à faire remonter en
   * production une erreur de rendu depuis `/library/...`, une page qui ne
   * montre aucun piano.
   *
   * Le verrou ne se relâche pas au départ du studio : `PianoViewport2` reste
   * monté et garde sa place dans l'arbre React, pour la même raison que
   * `ChordsProvider` plus bas — le démonter reposerait la piste piano sur une
   * ancienne encore en cours de démontage.
   *
   * Une exception, voulue : la page d'une prise, qui prépare son replay derrière
   * elle (`behind`). Les caméras ne se montent qu'une fois le rouleau rendu, et
   * c'est leur image qu'attend le bouton « Regarder ».
   */
  const [canvasBooted, setCanvasBooted] = useState(visible || behind);

  useEffect(() => {
    if (!visible && !behind) {
      return;
    }

    setCanvasBooted(true);
  }, [visible, behind]);

  const measuresHidden = replaying ? replay.measuresHidden : (recitalId !== null && (live?.measuresHidden ?? false));

  useEffect(() => {
    studio?.setMeasureLinesVisible(!measuresHidden);
  }, [measuresHidden, studio]);

  const [recitalSidebar, setRecitalSidebar] = useState<{ recitalId: string; open: boolean } | null>(null);
  /**
   * Repliée en récital comme devant un replay.
   *
   * Un récital se joue : la barre latérale prendrait de la place au rouleau et
   * ne servirait à rien pendant qu'on joue. Un replay se regarde, et c'est
   * d'abord le rouleau qu'on vient voir (décidé le 2026-09-27 ; il partait
   * ouvert sur grand écran pour montrer les prises). La poignée de la barre
   * d'outils la rouvre, voir `toolbar.tsx`. Ni l'un ni l'autre ne touche à la
   * préférence, qui revient telle quelle au morceau suivant.
   */
  const [replaySidebar, setReplaySidebar] = useState<{ recitalId: string; open: boolean } | null>(null);
  const replayId = replay.recitalId;
  const sidebarOpen = replayId !== null
    ? replaySidebar?.recitalId === replayId && replaySidebar.open
    : recitalId !== null
      ? recitalSidebar?.recitalId === recitalId && recitalSidebar.open
      : resolvedDefaults.sidebarPref ?? !panelsCompact;

  const setSidebarOpen = useCallback((open: boolean) => {
    if (replayId !== null) {
      setReplaySidebar({ recitalId: replayId, open });

      return;
    }

    if (recitalId !== null) {
      setRecitalSidebar({ recitalId, open });

      return;
    }

    window.localStorage.setItem(SIDEBAR_PREF_KEY, String(open));
    setResolvedDefaults((previous) => ({ ...previous, sidebarPref: open }));
  }, [recitalId, replayId]);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen(!sidebarOpen);
  }, [setSidebarOpen, sidebarOpen]);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, [setSidebarOpen]);

  const { isAccountModalOpen } = useAccountModal();
  // Lu pour la seule mesure : un inconnu et un inscrit qui revient ne voient
  // pas le même écran d'accueil, et l'entonnoir doit pouvoir se filtrer sur
  // les inconnus, qui en sont le sujet.
  const { isAuthenticated } = useAuth();

  /**
   * Le morceau sous la tête de lecture — lu, et non posé.
   *
   * C'était un état de cette page, écrit par chaque chemin de chargement. Il
   * vit maintenant dans la séquence de morceaux du studio (`SongSequence`),
   * qui le publie et le fait suivre à la tête de lecture quand elle passe
   * d'un morceau au suivant. Tout ce que la page en dérive — le titre, la
   * tonalité, l'analyse, les sections, la fiche, les parcours — suit sans
   * qu'aucune de ces surfaces ait à savoir qu'il y a une séquence. Les
   * chemins de chargement, eux, demandent à la séquence (`replace`) au lieu
   * de poser l'état.
   */
  const sequence = studio?.getSequence() ?? null;
  const songControler = useObservable(sequence?.current ?? NO_SONG);

  /**
   * Le niveau au-dessus des sections, quand le morceau en a un : les parties
   * d'un enchaînement, les mouvements. Vide partout ailleurs.
   *
   * Les prises d'un récital enregistré n'y sont **pas** : ce sont des morceaux
   * à part entière, posés dans la séquence du studio (`replay-context.tsx`),
   * chacun avec ses ticks depuis zéro et son analyse. Voir `AnalysisRun`.
   */
  const runs = useMemo<AnalysisRun[]>(
    () => (songControler ? songControler.getSong().getRuns() : []),
    [songControler],
  );
  /**
   * Analyse brute du morceau ouvert, pour le banc d'essai des niveaux
   * (`/studio/lab`).
   *
   * Gardée telle quelle parce qu'un niveau se régénère depuis elle et que
   * `Song.getAnalysis()` ne suffit pas : il porte les accords et les sections,
   * pas le tempo, pas les pistes, pas les notes. Reconstruire un morceau
   * demande les trois.
   */
  const [sourceAnalysis, setSourceAnalysis] = useState<AnalysisResult | null>(null);
  const [labBusy, setLabBusy] = useState(false);
  /**
   * Le niveau dont les notes sont **chargées**, ou `null` pour la partition.
   *
   * C'est un fait sur le morceau sous les mains, et non sur ce que le joueur
   * est en train de faire : les deux se ressemblent tant qu'on est dans le
   * parcours harmonique, et divergent dès qu'on en sort. Quitter un niveau
   * pour le jeu libre efface l'engagement et laisse la réduction chargée ; si
   * ce fait-là partait avec lui, le parcours par cœur lancé ensuite
   * n'apprendrait pas le morceau mais la réduction, sous le titre du morceau.
   * C'est exactement le mensonge que `handleLearnByHeart` existe pour
   * empêcher, et il a besoin de cette variable pour le voir.
   *
   * Rien ici ne rejoue les notes : elles sont déjà dans le studio, posées par
   * le geste qui a ouvert le niveau.
   */
  const [loadedRung, setLoadedRung] = useState<HarmonyContext | null>(null);
  /**
   * Le morceau qu'un exercice a remplacé, pour y revenir.
   *
   * Retiré de la séquence sans être détruit (`withdraw`) : ses notes et son
   * synthé attendent le « retour ». Il appartient donc à cette page tant
   * qu'il est là, et c'est elle qui le jette (`discard`) quand un autre
   * chargement rend le retour impossible — sans quoi il resterait chargé en
   * mémoire jusqu'à la fermeture de l'onglet.
   */
  const [previousSongControler, setPreviousSongControler] = useState<SongControler | null>(null);
  const [isGenerated, setIsGenerated] = useState(false);
  /**
   * L'exercice sous les mains, pour l'enveloppe de session du studio.
   *
   * `isGenerated` dit qu'un morceau généré est chargé, il ne dit pas lequel —
   * et le panneau de génération en produit qui ne sont pas des exercices du
   * catalogue. La session porte donc l'identifiant quand il existe, et rien
   * quand il n'existe pas, plutôt que celui de l'exercice d'avant.
   */
  const [loadedExerciseId, setLoadedExerciseId] = useState<string | null>(null);
  const [sourceMidiId, setSourceMidiId] = useState<string | null>(null);
  // Identité *catalogue* du morceau chargé, distincte de `sourceMidiId` :
  // c'est la seule qui sache le recharger (`loadPieceFromCatalog`). Le
  // répertoire la garde pour pouvoir reproposer le morceau plus tard ; un
  // import qui n'est passé par aucune fiche n'en a pas, et n'entre donc pas
  // dans la file — mieux vaut ne rien proposer qu'une ligne qui n'ouvre rien.
  //
  // C'est aussi **la seule identité de morceau** que cet écran distribue à la
  // mesure, et elle est posée par `loadPieceFromCatalog` à partir de l'argument
  // même qui part dans `piece_opened.pieceId`. Trois consommateurs, une valeur :
  // `piece_opened.pieceId`, `studio_session_ended.pieceId` (via
  // `useStudioSession`) et le `pieceId` du parcours d'apprentissage. Joindre
  // une ouverture à la séance qu'elle a produite, puis à ce que le parcours en
  // a fait, se fait donc par égalité simple, sans table de correspondance.
  //
  // `learnMorceauId`, plus bas, ne la remplace jamais : c'est une clé de
  // *progression*, dérivée de `sourceMidiId` et suffixée par la transposition
  // et le niveau ouvert, donc plusieurs clés pour un même morceau. Elle sert à
  // retrouver un état d'avancement, pas à nommer une œuvre.
  const [sourcePieceId, setSourcePieceId] = useState<string | null>(null);
  // Écart cumulé, en demi-tons, entre le morceau affiché et le fichier
  // importé. Reste à 0 tant qu'on n'a pas transposé.
  const [transposeSemis, setTransposeSemis] = useState(0);
  /**
   * La plage sélectionnée dans le navigateur. Sélectionner n'est pas boucler :
   * un clic sur une mesure la choisit et s'y rend, `looping` dit si le
   * transport doit y tourner.
   */
  const [loop, setLoop] = useState<LoopRange | null>(null);
  const [looping, setLooping] = useState(false);
  // PieceOverview is a transient overlay shown right after a piece is
  // imported. It overrides the studio overlay slot regardless of the
  // current engagement. Local state is enough, no global store
  // needed for a single boolean tied to a one-shot UI moment.
  const [pieceOverviewOpen, setPieceOverviewOpen] = useState(false);
  /**
   * Ce que la fiche montre : le morceau, ou le choix du parcours.
   *
   * L'entrée du programme a été une vue du studio, `porch`, avec sa route et
   * son écran. Elle doublait la fiche : même titre, même compositeur, même
   * tonalité, et la fiche portait déjà « Apprendre ». Deux écrans pour une
   * seule porte, dont le second effaçait le premier.
   *
   * C'est donc un état de la fiche, et pas un écran de plus. Il vit ici et non
   * dans la fiche parce que la barre du studio l'ouvre aussi, depuis un endroit
   * qui ne connaît pas la fiche.
   */
  const [pieceOverviewPhase, setPieceOverviewPhase] =
    useState<PieceOverviewPhase>("piece");
  // Settings overlay, same kind of transient UI (overrides the slot
  // overlay), triggered by the AppHeader gear button. Mutually exclusive
  // with `pieceOverviewOpen` so opening one closes the other. State is
  // lifted to studio-context so the AppHeader (mounted above this page)
  // can drive it.
  const [settingsOpen, setSettingsOpen] = useStudioSettingsOpen();
  // Stable : une lambda inline ici annulait le `memo` de la barre d'outils à
  // chaque rendu de la page.
  const handleOpenSettings = useCallback(() => {
    setSettingsOpen(true);
  }, [setSettingsOpen]);

  // Non-blocking toast surfaced at the Studio mount when the back returned
  // `quality.kind === "ok_with_hints"`. Lives outside the clamped overlay
  // slots so it always floats top-center (see spec §3.1). Auto-hide and
  // dismiss are owned by the `ToastBanner` atom.
  const [okWithHintsToastMessage, setOkWithHintsToastMessage] = useState<
    string | null
  >(null);

  // ── Empty-state welcome overlay ──
  // Shown when no piece is loaded AND we're in "free" mode (the only mode
  // that makes sense without a score). Fade-out is delayed by `GRACE_MS`
  // after the first note (anti-frustration), and re-appearance is modulated
  // by how much the user has actually played:
  //   - never played          → REAPPEAR_DEFAULT_MS
  //   - light activity        → REAPPEAR_LIGHT_MS (more breathing room)
  //   - engaged (>30 s)       → don't auto-reappear; recall pill stays
  // Counters are reset whenever the card is shown again, so each "session
  // around the card" is graded independently.
  const [emptyOverlayVisible, setEmptyOverlayVisible] = useState(true);

  /**
   * Spectateur d'un récital : le studio se regarde, il ne se pilote pas.
   *
   * Ce que le contexte live refuse déjà — commandes, émission, navigation du
   * rouleau —, la page le complète par ce qu'elle seule tient : la barre
   * d'outils, la barre latérale, le dépôt de fichier, l'écran d'accueil et le
   * panneau de génération. Ce sont les portes par lesquelles un morceau entre
   * dans le studio, et un spectateur regarde la pièce de l'interprète : il
   * n'en charge pas une autre par-dessus.
   *
   * Restent ouvertes, parce qu'elles ne sont pas d'ici : le header, qui mène
   * à la bibliothèque — en revenir avec un morceau le chargerait —, et la
   * fiche d'un morceau déjà ouvert avant d'entrer. Deux détours qu'il faut
   * vouloir faire ; les verrous ci-dessus ferment ce qu'un geste ordinaire
   * atteint.
   */
  const spectator = (live !== null && live.isListener) || replay.recitalId !== null;
  // La préférence de repli n'est pas touchée : la barre latérale revient
  // telle qu'elle était quand on quitte le récital.
  //
  // Devant un replay, la barre ne s'ouvre que si la prise a été analysée :
  // sans analyse elle n'a rien à dire — pas de section à montrer — et le
  // sélecteur vide dirait « importe un fichier MIDI » à quelqu'un qui regarde
  // son récital.
  const sidebarShown = sidebarOpen && (!spectator || (replayId !== null && replay.analyzed));
  const [pendingUploadFile, setPendingUploadFile] = useState<File | null>(null);
  const [dragKind, setDragKind] = useState<DroppedFileKind | null>(null);
  const [errorActive, setErrorActive] = useState(false);
  const [errorKind, setErrorKind] = useState<DropErrorKind>("unknown");
  const overlayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const graceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const noteCountRef = useRef(0);
  const firstNoteTimeRef = useRef<number | null>(null);
  const lastNoteTimeRef = useRef<number | null>(null);

  const cancelOverlayReappear = useCallback(() => {
    if (overlayTimerRef.current !== null) {
      clearTimeout(overlayTimerRef.current);
      overlayTimerRef.current = null;
    }
  }, []);

  const cancelGrace = useCallback(() => {
    if (graceTimerRef.current !== null) {
      clearTimeout(graceTimerRef.current);
      graceTimerRef.current = null;
    }
  }, []);

  const resetActivityCounters = useCallback(() => {
    noteCountRef.current = 0;
    firstNoteTimeRef.current = null;
    lastNoteTimeRef.current = null;
  }, []);

  const scheduleOverlayReappear = useCallback(() => {
    // En séance, le silence n'est pas de l'inactivité : on écoute. Faire
    // revenir l'écran d'accueil et geler le transport couperait le roll
    // pendant que le partenaire joue.
    //
    // Devant un replay non plus : on regarde quelqu'un jouer, et ne rien
    // toucher pendant huit secondes est exactement ce qu'on vient faire. Le
    // gel serait d'ailleurs muet — l'écran d'accueil ne se montre pas quand un
    // morceau est chargé, et un replay en charge un. Il ne resterait qu'un
    // rouleau arrêté, sans son et sans explication.
    if (onAir || replayId !== null) {
      return;
    }

    cancelOverlayReappear();

    const first = firstNoteTimeRef.current;
    const last = lastNoteTimeRef.current;
    const activityMs = first !== null && last !== null ? last - first : 0;

    // Engaged user: leave them alone, the recall pill is enough.
    if (activityMs > ENGAGED_THRESHOLD_MS) {
      return;
    }

    const delay =
      activityMs > 0 && activityMs < LIGHT_THRESHOLD_MS
        ? REAPPEAR_LIGHT_MS
        : REAPPEAR_DEFAULT_MS;

    overlayTimerRef.current = setTimeout(() => {
      // The user has gone idle, bring back the welcome copy and pause
      // the transport so the piano-roll stops advancing into emptiness.
      studio?.getLocalTransport().detach();
      setEmptyOverlayVisible(true);
      resetActivityCounters();
      overlayTimerRef.current = null;
    }, delay);
  }, [cancelOverlayReappear, onAir, replayId, resetActivityCounters, studio]);

  useEffect(() => {
    if (!onAir || studio === null || studio === undefined) {
      return;
    }

    // Entrer en séance range l'écran d'accueil et défait ce qu'une inactivité
    // avait pu programmer.
    cancelOverlayReappear();
    setEmptyOverlayVisible(false);

    // Et remet la vue au direct. Sans ça, on entre dans une séance avec un
    // transport local figé par une pause d'inactivité, et le partenaire semble
    // jouer dans le futur — ses notes portent des positions que notre vue a
    // cessé de suivre.
    studio.getLocalTransport().followShared();
  }, [cancelOverlayReappear, onAir, studio]);

  // ── Boot phase: welcome card → jingle → ready ──
  // First-ever visit shows a welcome card. Click "Entrer dans le studio"
  // plays the Virtuosa jingle in the piano-roll, then drops to the empty
  // state. Subsequent visits skip straight to "ready".
  const [bootPhase, setBootPhase] = useState<BootPhase>("checking");
  // Whether the jingle preload has resolved (success or failure). Drives
  // the welcome card "Entrer" button enable/disable and its inline loader.
  const [jingleReady, setJingleReady] = useState(false);
  const jingleControlerRef = useRef<SongControler | null>(null);
  const jingleEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Holds the in-flight jingle preload (fetch + Song build + SongControler
  // load including sample download). Started while the welcome card is up
  // so the transition "click Entrer → first note" is seamless.
  const jinglePreloadRef = useRef<Promise<{
    controler: SongControler;
    durationMs: number;
  }> | null>(null);
  // La garde contre un second préchargement est ce booléen, pas la promesse :
  // celle-ci est vidée à la fin du jingle pour que son `SongControler` et sa
  // `Song` (échantillons compris) partent au ramasse-miettes.
  const jinglePreloadStartedRef = useRef(false);

  // ── Mesure de l'entonnoir d'entrée ──────────────────────────────
  //
  // Déclaré ici, avant les gestionnaires : ce sont eux qui rapportent les
  // portes, et une dépendance déclarée plus bas ne serait pas encore liée
  // quand leur `useCallback` évalue ses dépendances.
  //
  // La page est la seule surface à connaître tout ce qui fait qu'un écran est
  // réellement sous les yeux : la route (`StudioPage` reste monté, seulement
  // masqué, sur toute la section), la vue, l'engagement, la phase de démarrage
  // et l'absence de morceau. L'overlay, lui, ne connaît que sa propre opacité.
  const emptyStudioMounted =
    visible &&
    studioView === "roll" &&
    engagement === null &&
    bootPhase === "ready" &&
    !songControler;

  // Relu au répertoire plutôt que pris à `offerRef` : ce dernier n'est rempli
  // que par `readResume`, appelé par l'overlay à son montage, donc après que
  // la page a constaté l'exposition — la seule exposition qui compte, celle de
  // l'arrivée, s'en trouverait rapportée sans offre. Même source, même règle,
  // même instant : c'est une lecture, pas un second arbitrage.
  const hasResumeOffer = useCallback(
    () => repertoire().offer(Date.now()) !== null,
    [],
  );

  const entryFunnel = useStudioEntryFunnel({
    welcomeVisible: visible && bootPhase === "welcome",
    emptyMounted: emptyStudioMounted,
    emptyOffered: emptyOverlayVisible,
    authenticated: isAuthenticated,
    hasResumeOffer,
  });

  // ── L'enveloppe de session du studio ────────────────────────────
  //
  // Le dénominateur de tout l'usage du studio, qui manquait : un visiteur
  // resté une demi-heure à jouer et un visiteur reparti aussitôt se
  // ressemblaient trait pour trait. Elle vient **à côté** de
  // `exercise-session` et des `practice_session_*` du parcours, pas à leur
  // place : eux décrivent l'apprentissage, elle décrit le studio.
  //
  // La phase de démarrage compte autant que la route : pendant le jingle,
  // c'est l'application qui joue, et ouvrir la session là ferait entrer son
  // rite d'accueil dans le temps de jeu de la personne.
  const studioSessionContext = studioSessionContextOf(
    visible && bootPhase === "ready",
    songControler !== null,
    isGenerated,
  );

  useStudioSession({
    context: studioSessionContext,
    owner: songControler,
    // Chaque identité sur son contexte, et jamais celle d'avant : un exercice
    // chargé par-dessus un morceau laisse `sourcePieceId` en place, et le
    // recopier ferait porter la session d'exercice par l'identité de la pièce
    // qu'il a remplacée.
    pieceId:
      studioSessionContext === "piece" ? (sourcePieceId ?? undefined) : undefined,
    exerciseId:
      studioSessionContext === "exercise"
        ? (loadedExerciseId ?? undefined)
        : undefined,
  });

  // Resolve the phase the studio boots into. The welcome card is shown on
  // every reload by default (so the jingle plays again) unless the user has
  // explicitly disabled it from Settings. Reads localStorage, so it can only
  // run on the client, hence the "checking" placeholder during SSR.
  //
  // Un replay entre sans carton ni jingle : on y arrive d'un clic — « Regarder »
  // sur la page d'une prise partagée —, qui a déjà réveillé l'audio
  // (`unlockAudioOnGesture` écoute la fenêtre). Le rite d'entrée du studio
  // vide n'a rien à dire à qui vient voir quelqu'un jouer, et le lui imposer
  // faisait trois clics et un jingle avant la première note (2026-09-25).
  // (`replaying` est lu plus haut, avec les lignes de mesure.)

  const resolveInitialPhase = useCallback((): BootPhase => {
    if (typeof window === "undefined" || replaying) {
      return "ready";
    }

    const disabled = window.localStorage.getItem(WELCOME_DISABLED_LS_KEY) === "1";

    return disabled ? "ready" : "welcome";
  }, [replaying]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    setBootPhase(resolveInitialPhase());
  }, [resolveInitialPhase]);

  // Un morceau chargé congédie le carton d'accueil.
  //
  // `StudioPage` est monté sur toute la section `(main)`, seulement masqué hors
  // du studio : quelqu'un qui charge le site *directement* sur la fiche d'un
  // morceau n'a donc jamais vu le carton, et `bootPhase` vaut encore "welcome"
  // quand « Ouvrir ce morceau » pousse vers `/studio`. Le carton s'affichait
  // alors par-dessus le morceau déjà chargé, et « Entrer dans le studio »
  // lançait le jingle *avec* lui — deux pistes sur le même transport, puis un
  // `resetTimeline()` de fin de jingle qui rendait au transport le tempo par
  // défaut pendant que la barre affichait celui du morceau.
  //
  // La règle est plus simple que le symptôme : le carton est le rite d'entrée
  // d'un studio vide, il n'a rien à dire à qui tient déjà un morceau.
  useEffect(() => {
    if (songControler === null || bootPhase === "ready") {
      return;
    }

    setBootPhase("ready");
  }, [songControler, bootPhase]);

  // Une séance live congédie aussi le carton d'accueil, et pour la même raison
  // qu'un morceau : c'est le rite d'entrée d'un studio vide, et son clic lance
  // le jingle sur le transport — celui que la séance partage. L'interprète
  // qui arrive de la salle d'attente jouerait le jingle à son public, et le
  // spectateur n'a rien à « entrer » : il est déjà entré, d'un clic dans la
  // salle, qui a aussi réveillé l'audio (`unlockAudioOnGesture` écoute la
  // fenêtre, pas le studio). Seul le carton se retire : un jingle déjà lancé
  // se termine tout seul, et sa fin range sa piste.
  const liveJoining = live !== null && live.status !== "off" && live.status !== "error";

  useEffect(() => {
    if (!(liveJoining || replaying) || bootPhase !== "welcome") {
      return;
    }

    setBootPhase("ready");
  }, [liveJoining, replaying, bootPhase]);

  // L'entrée est finie, et le studio le dit : ce qui se charge tout seul en
  // arrivant a attendu jusque-là plutôt que de sonner avec le jingle. Un seul
  // endroit pour tous les chemins — jingle joué, sauté, ou jamais lancé —,
  // parce que la phase dit déjà lequel a été pris (voir `Studio.onReady`).
  //
  // Et le reprend en partant : le studio survit à cette page, et celle qui la
  // remplacera mènera sa propre entrée (voir `Studio.markEntering`).
  useEffect(() => {
    if (bootPhase !== "ready" || !studio) {
      return;
    }

    studio.markReady();

    return () => studio.markEntering();
  }, [bootPhase, studio]);

  // Preload the jingle while the welcome card is on screen. By the time
  // the user clicks "Entrer dans le studio", samples are downloaded and
  // the SongControler is ready, clicking only adds the track and starts
  // the transport, no perceptible gap before the first note.
  useEffect(() => {
    // `visible` compte autant que la phase de démarrage, et c'est ce qui
    // manquait : `StudioPage` est monté sur *toutes* les routes de `(main)`,
    // caché sur celles qui ne sont pas le studio. Arriver sur `/library`
    // préchargeait donc le jingle — `GET /api/jingle` plus les 60 échantillons
    // de piano, ~1,3 Mo au réseau et une banque décodée en mémoire — pour un
    // carton d'accueil enfermé dans le conteneur `hidden`, sur lequel personne
    // ne pourra jamais cliquer.
    //
    // La bibliothèque est la première page d'un visiteur : c'est exactement là
    // qu'il ne fallait pas payer ça. Le préchargement se rallume tout seul en
    // arrivant sur le studio, `bootPhase` valant encore « welcome ».
    if (!visible || !studio || bootPhase !== "welcome") {
      return;
    }

    if (jinglePreloadStartedRef.current) {
      return;
    }

    jinglePreloadStartedRef.current = true;
    jinglePreloadRef.current = (async () => {
      const t0 = performance.now();
      logJinglePreload("start");

      const result = await getJingle();
      logJinglePreload(`/api/jingle ok in ${Math.round(performance.now() - t0)}ms, ${result.notes.length} notes, ${result.tracks.length} tracks`);

      const song = createSongFromRawAnalysis(result);
      logJinglePreload(`song built (${song.getParts().length} parts) at ${Math.round(performance.now() - t0)}ms`);

      const controler = new SongControler(song, studio.getTransport(), studio.getAudioContext());
      const tLoad = performance.now();
      logJinglePreload("controler.load(), loading samples for", song.getParts().length, "synth(s)…");

      await controler.load();
      logJinglePreload(`controler.load() done in ${Math.round(performance.now() - tLoad)}ms (total ${Math.round(performance.now() - t0)}ms)`);

      return { controler, durationMs: computeJingleDurationMs(result) };
    })();

    jinglePreloadRef.current
      .then(() => setJingleReady(true))
      .catch((err) => {
        console.warn("Jingle preload failed:", err);
        setJingleReady(true);
      });
  }, [visible, studio, bootPhase]);

  // End-of-jingle cleanup. Pauses the transport, stops scheduled audio,
  // tears down the temporary track, and reveals the empty-state overlay.
  // Idempotent: safe to call from the timer *and* from the skip listener.
  const handleJingleEnd = useCallback(() => {
    if (jingleEndTimerRef.current !== null) {
      clearTimeout(jingleEndTimerRef.current);
      jingleEndTimerRef.current = null;
    }

    if (studio) {
      // Always pause + rewind, even if the controler was already torn
      // down (e.g. user skip after the timer fired), leaving the
      // transport advancing into emptiness right after the jingle gives
      // a "ghost" replay when the user starts a new session.
      studio.getLocalTransport().detach();
      studio.stopAllScheduledNotes();
      studio.getLocalTransport().seek(0);

      // The jingle installed its own (90 BPM) timeline on the transport; the
      // empty studio that follows has no piece, so restore the default
      // timeline. Otherwise the synced metronome (which follows the transport
      // tempo) would click at the jingle tempo while the UI BPM shows 120.
      //
      // Sauf s'il y a un morceau : le défaut n'est alors pas plus juste que le
      // tempo du jingle, il est faux autrement. La timeline du transport
      // appartient au morceau chargé, et l'écraser ici le ferait jouer à 120
      // pendant que la barre annonce son tempo à lui.
      //
      // Lu dans la séquence et non dans `songControler` : ce rappel part d'un
      // minuteur armé au lancement du jingle, et sa fermeture ne voit pas un
      // morceau posé depuis.
      if (studio.getSequence().current.value === null) {
        studio.resetTimeline();
      }

      const controler = jingleControlerRef.current;

      if (controler) {
        const jingleTrack = studio
          .getTrackManager()
          .getTracks()
          .find((t) => t.getControler() === controler);

        if (jingleTrack) {
          studio.removeTrack(jingleTrack);
        }
      }
    }

    jingleControlerRef.current = null;
    // Le jingle ne rejoue jamais : lâcher la promesse libère son contrôleur,
    // sa `Song` et ses échantillons, sinon retenus toute la session.
    jinglePreloadRef.current = null;
    setBootPhase("ready");
    setEmptyOverlayVisible(true);
    resetActivityCounters();
  }, [studio, resetActivityCounters]);

  const handleEnterStudio = useCallback(async () => {
    // Only the welcome card may boot the studio. Replaying the jingle from
    // any later phase re-adds its track, hijacks the transport and resets
    // the timeline of an already-loaded piece.
    if (bootPhase !== "welcome") {
      return;
    }

    entryFunnel.entered();
    setBootPhase("jingle");

    if (!studio) {
      setBootPhase("ready");

      return;
    }

    // The studio's AudioContext is created at mount, before any user
    // gesture, so the browser refuses the boot-time resume() and leaves it
    // "suspended" with currentTime frozen. This click is the first user
    // gesture: resume the context here, synchronously inside the activation,
    // before any await. Without it the transport clock never advances, so
    // the jingle is drawn but the roll stays still and no audio plays.
    // (clock.start() can't do it: its isRunning guard short-circuits since
    // it already ran at mount, so we resume the context directly.)
    //
    // La promesse de `resume()` est lancée et jamais attendue : WebKit
    // l'accroche à l'état `running`, qu'un contexte interrompu peut ne plus
    // jamais atteindre, et l'attendre gelait l'entrée dans le studio pour de
    // bon. C'est l'**état** qu'on attend, avec une limite : le contexte sain
    // rend la main en une image, celui qui traîne laisse partir le jingle en
    // silence plutôt que de retenir l'écran. Voir `whenAudioRunning`.
    const audioContext = studio.getAudioContext();

    void audioContext.resume().catch((err: unknown) => {
      console.warn("AudioContext resume failed:", err);
    });

    await whenAudioRunning(audioContext, AUDIO_WAKE_TIMEOUT_MS);

    // Reuse the preload kicked off while the welcome card was up. If for
    // any reason it didn't start (e.g. studio mounted late), fire it now
    // so we never leave the user staring at silence.
    if (jinglePreloadRef.current === null) {
      jinglePreloadStartedRef.current = true;
      jinglePreloadRef.current = (async () => {
        const result = await getJingle();
        const song = createSongFromRawAnalysis(result);
        const controler = new SongControler(song, studio.getTransport(), studio.getAudioContext());
        await controler.load();

        return { controler, durationMs: computeJingleDurationMs(result) };
      })();
    }

    try {
      const { controler, durationMs } = await jinglePreloadRef.current;

      studio.addTrack(controler);
      jingleControlerRef.current = controler;

      // Notes are drawn immediately (addTrack above), but we want a small
      // breathing room before the first audible note. Roll the playhead
      // back so it glides toward tick 0 over JINGLE_START_DELAY_MS and
      // then naturally lands on the first scheduled note, no setTimeout
      // around play() means the visual playback starts right away.
      const transport = studio.getLocalTransport();
      transport.seek(-JINGLE_START_DELAY_MS / 1000);
      transport.play();

      jingleEndTimerRef.current = setTimeout(
        () => handleJingleEnd(),
        JINGLE_START_DELAY_MS + durationMs + JINGLE_END_BUFFER_MS,
      );
    } catch (err) {
      console.warn("Jingle failed to play:", err);
      setBootPhase("ready");
    }
  }, [studio, bootPhase, handleJingleEnd, entryFunnel]);

  // Skip the jingle on any user input. Armed with a small delay so the
  // click that triggered "Entrer dans le studio" doesn't immediately skip.
  useEffect(() => {
    if (bootPhase !== "jingle") {
      return;
    }

    let armed = false;
    const armTimer = setTimeout(() => {
      armed = true;
    }, JINGLE_SKIP_ARM_DELAY_MS);

    const handleSkip = () => {
      if (!armed) {
        return;
      }

      handleJingleEnd();
    };

    window.addEventListener("click", handleSkip);
    window.addEventListener("keydown", handleSkip);

    return () => {
      clearTimeout(armTimer);
      window.removeEventListener("click", handleSkip);
      window.removeEventListener("keydown", handleSkip);
    };
  }, [bootPhase, handleJingleEnd]);

  // Cleanup the jingle timer on unmount.
  useEffect(() => {
    return () => {
      if (jingleEndTimerRef.current !== null) {
        clearTimeout(jingleEndTimerRef.current);
      }
    };
  }, []);

  // ── Keyboard shortcut: ⌘K / Ctrl+K ──
  useEffect(() => {
    if (!visible) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();

        // Un exercice généré est un morceau qui entre dans le studio : pas en
        // spectateur. Le raccourci est absorbé plutôt que laissé au navigateur.
        if (spectator) {
          return;
        }

        setRightPanel((prev) => (prev === "generate" ? null : "generate"));
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [spectator, visible]);

  const songInfo = useMemo(() => {
    if (!songControler) {
      return null;
    }

    const song = songControler.getSong();
    const timeline = song.getMusicalTimeline();
    const ts = timeline.getTimeSignatureAtTick(0);

    return {
      title: song.getTitle(),
      composer: song.getComposer(),
      timeSignature: `${ts.numerator}/${ts.denominator}`,
      // Structured form, used by the toolbar to lock the metronome signature
      // picker on the loaded piece. Kept alongside the existing string label
      // for backward compatibility with consumers that just want to display it.
      timeSignatureSig: { num: ts.numerator, den: ts.denominator },
      bpm: timeline.getBpmAtTick(0),
      difficulty: song.getDifficulty()?.overall,
    };
  }, [songControler]);

  const sourceTonality = useMemo<Tonality | null>(() => {
    if (!songControler) {
      return null;
    }

    return tonalityFromDetectedKey(songControler.getSong().getKey());
  }, [songControler]);

  // Identité de persistance du parcours d'apprentissage.
  //
  // Transposer réanalyse le morceau sans créer d'entrée en bibliothèque :
  // même `sourceMidiId`, mais toutes les hauteurs décalées, donc un plan de
  // phrases différent (`hashPhrasePlan` hache les midis). Sans ce suffixe,
  // la variante et l'original partagent la même clé de progression et se
  // marchent dessus. On en fait donc deux entrées distinctes ; revenir à la
  // tonalité d'origine ramène l'écart à 0, donc la clé d'origine, donc la
  // progression initiale, intacte.
  const learnMorceauId = useMemo<string | null>(() => {
    if (sourceMidiId === null) {
      return null;
    }

    const transposed = transposeSemis === 0 ? sourceMidiId : `${sourceMidiId}:t${transposeSemis}`;

    // Et un niveau de l'échelle est une progression de plus, pour la même
    // raison qu'une transposition : ses notes ne sont pas celles du morceau,
    // donc son plan de phrases est un autre plan. Sans le suffixe, le magasin
    // trouverait une entrée dont le hash ne correspond pas et repartirait de
    // zéro à chaque aller-retour, en effaçant celle du morceau au passage.
    return loadedRung === null ? transposed : `${transposed}:n${loadedRung.rungId}`;
  }, [sourceMidiId, transposeSemis, loadedRung]);

  // Le transport ne joue pas sous la modale de compte.
  //
  // `isAccountModalOpen` etait expose par le contexte et consomme par
  // personne : depuis le declencheur de fin de seance, quelqu un lisait un
  // formulaire pendant que son morceau tournait derriere.
  //
  // Pause, jamais stop : le morceau reste charge, le curseur reste ou il est.
  // Et surtout aucune reprise automatique a la fermeture — relancer le
  // transport sous les doigts de quelqu un est plus desagreable que de le
  // laisser appuyer sur lecture.
  useEffect(() => {
    if (!isAccountModalOpen || !studio) {
      return;
    }

    studio.getLocalTransport().detach();
  }, [isAccountModalOpen, studio]);

  // Sync detected key → studio tonality whenever a piece is loaded.
  // When no piece is loaded, leave the tonality alone (user may have picked one manually).
  useEffect(() => {
    if (!studio || !sourceTonality) {
      return;
    }

    studio.setTonality(sourceTonality);
  }, [studio, sourceTonality]);

  // Sync the loaded piece's intrinsic tempo into the Studio so the BPM
  // popover (and any future tempo-aware UI) shares a single source of truth.
  useEffect(() => {
    if (!studio || !songInfo) {
      return;
    }

    studio.setOriginalBpm(songInfo.bpm);
  }, [studio, songInfo]);

  // Reset the empty-state overlay whenever a song is loaded/unloaded.
  //   Loaded   : hide it permanently (the toolbar takes over).
  //   Unloaded : show again, the user is back to free-play.
  // We also reset activity counters on unload so the new session grades
  // engagement from zero.
  useEffect(() => {
    cancelOverlayReappear();
    cancelGrace();

    if (songControler) {
      setEmptyOverlayVisible(false);

      return;
    }

    setEmptyOverlayVisible(true);
    resetActivityCounters();
  }, [songControler, cancelOverlayReappear, cancelGrace, resetActivityCounters]);

  // Sans morceau, aucun parcours n'a de sens : les deux travaillent une
  // partition. On repasse donc au jeu libre, pour que la condition de
  // l'overlay tienne et que la barre n'expose pas un engagement resté de la
  // session d'avant.
  useEffect(() => {
    if (!studio || songControler) {
      return;
    }

    studio.setEngagement(null);
    // Drop any lingering tempo/signature from the unloaded piece so the empty
    // studio's transport matches the default UI BPM (keeps the metronome in
    // sync with what the toolbar shows).
    studio.resetTimeline();
  }, [studio, songControler]);

  // Un morceau qui s'installe trouve le rouleau vide.
  //
  // La lane du clavier est cumulative par construction : en jeu libre, revoir
  // ce qu'on vient de jouer est exactement le service rendu. Au chargement,
  // ça s'inverse — le gris de la trace se pose sur la notation qui s'ouvre, et
  // on lit son propre passé au lieu du morceau. Ce qui restait armé de la
  // session d'avant part avec : une boucle sur les mesures 5 à 8 se remettrait
  // à tourner ici, sur huit mesures qui n'ont rien à voir, avec son voile et
  // ses bornes sur le roll ; une sélection d'indices assombrirait des notes
  // choisies dans une autre partition.
  //
  // Ici plutôt que dans chaque chemin de chargement : import, catalogue,
  // génération, exercice, transposition et réduction de niveau n'ont qu'une
  // chose en commun, le `songControler` qui change, et c'est le seul endroit
  // où un chemin de plus est couvert d'avance. Refermer un morceau ne passe
  // pas par là (`songControler` devient `null`) : la trace du passage qu'on
  // vient de jouer reste à relire, c'est tout ce qui reste de lui.
  useEffect(() => {
    if (!studio || !songControler) {
      return;
    }

    studio.clearLivePiano();
    studio.clearHintSelection();
    setLoop(null);
    setLooping(false);
    studio.setLoop(null);
    studio.setWorkRange(null);
  }, [studio, songControler]);

  // Cleanup the timers on unmount.
  useEffect(() => {
    return () => {
      cancelOverlayReappear();
      cancelGrace();

      if (errorTimerRef.current !== null) {
        clearTimeout(errorTimerRef.current);
      }
    };
  }, [cancelOverlayReappear, cancelGrace]);

  // Listen to the piano while no piece is loaded.
  // First press while the card is *visible*: arm a 1.5 s grace timer
  //   before fading out, explorers tapping a key shouldn't lose the
  //   onboarding instantly. Further presses inside the grace window do
  //   not re-arm it (we let it expire naturally to keep the fade timing
  //   predictable).
  // Press while the card is *hidden* : just postpone the reappearance.
  useEffect(() => {
    if (!studio || songControler) {
      return;
    }

    const piano = studio.getPiano();

    return piano.on((event) => {
      if (event.type !== "press") {
        return;
      }

      const now = Date.now();
      noteCountRef.current += 1;

      if (firstNoteTimeRef.current === null) {
        firstNoteTimeRef.current = now;
      }

      lastNoteTimeRef.current = now;

      // Start the transport on the very first press so the player's notes
      // are captured into the piano-roll. Idempotent, only kicks in when
      // the transport is currently paused.
      //
      // Jamais devant un replay : une touche effleurée pendant qu'on regarde
      // n'est pas une demande de lecture, et le studio n'est pas vide — c'est
      // le récital de quelqu'un qui tourne.
      const transport = studio.getLocalTransport();

      if (transport.isDetached && replayId === null) {
        transport.play();
      }

      if (emptyOverlayVisible) {
        if (graceTimerRef.current === null) {
          graceTimerRef.current = setTimeout(() => {
            setEmptyOverlayVisible(false);
            scheduleOverlayReappear();
            graceTimerRef.current = null;
          }, GRACE_MS);
        }

        return;
      }

      scheduleOverlayReappear();
    });
  }, [studio, songControler, emptyOverlayVisible, scheduleOverlayReappear]);

  // Hide the welcome overlay when the transport starts via a count-in
  // (toolbar play button → `studio.startWithCountIn`). The CSS opacity
  // transition on `EmptyStudioOverlay` provides the fade so the count-in
  // can take over the canvas. Only relevant in free mode without a piece
  //, the same condition that gates the overlay's mount.
  useEffect(() => {
    if (!studio || songControler) {
      return;
    }

    return studio.countIn.on((event) => {
      if (event.type !== "start") {
        return;
      }

      cancelGrace();
      setEmptyOverlayVisible(false);
    });
  }, [studio, songControler, cancelGrace]);

  // Bring the welcome overlay back as soon as the transport ends up
  // paused at tick 0. Triggered by the toolbar Stop button (pause + seek 0)
  // but also covers any future code path landing on the same state.
  // Same gating as the overlay's mount, so we don't fight other phases
  // (jingle teardown already pauses + seeks 0 *before* flipping bootPhase
  // to "ready", so its events are not observed here).
  useEffect(() => {
    if (!studio || songControler || bootPhase !== "ready") {
      return;
    }

    const transport = studio.getLocalTransport();

    return transport.on((event) => {
      if (event.type !== "detach" && event.type !== "seek") {
        return;
      }

      if (!transport.isDetached || transport.currentTick() !== 0) {
        return;
      }

      // En séance, une vue figée à zéro n'est pas un studio à l'abandon :
      // c'est la séance elle-même qui est en pause, à son début — toute
      // séance naît ainsi, et l'interprète d'un récital y arrive avant de la
      // lancer. Rappeler l'écran d'accueil par-dessus, c'est proposer de
      // charger un morceau à quelqu'un qui est en train d'entrer en scène.
      if (onAir) {
        return;
      }

      cancelGrace();
      cancelOverlayReappear();
      resetActivityCounters();
      setEmptyOverlayVisible(true);
    });
  }, [
    studio,
    songControler,
    bootPhase,
    onAir,
    cancelGrace,
    cancelOverlayReappear,
    resetActivityCounters,
  ]);

  const handleTranspose = useCallback(
    async (target: Tonality) => {
      if (!studio) {
        return;
      }

      // Without a stored MIDI file we cannot re-run the analyzer, just
      // update the global tonality so the rest of the UI reflects the choice.
      if (!sourceMidiId || !sourceTonality || !songControler) {
        studio.setTonality(target);

        return;
      }

      const semis = shortestSemitones(sourceTonality, target);
      const previousSong = songControler.getSong();

      // Une variante transposée sort du répertoire, et c'est voulu.
      //
      // `learnMorceauId` gagne un suffixe `:t<n>` — c'est ce qui empêche la
      // variante et l'original de se marcher dessus — mais `pieceId` ne le
      // porte pas : le rechargement rouvrirait l'original. L'entrée pointerait
      // alors sur une progression qui n'est pas la sienne, et `playedThrough`
      // ne retrouverait plus sa clé, donc le niveau ne monterait jamais et le
      // morceau serait redû à perpétuité. Mieux vaut ne rien proposer.
      //
      // Revenir à la tonalité d'origine ne le restaure pas dans la séance ;
      // le prochain chargement s'en charge.
      setSourcePieceId(null);

      // Stateless re-analysis through the Symfony API, no new library entry.
      const analysis = await transposeMidi(sourceMidiId, semis);
      const newSong = createSongFromRawAnalysis(analysis);

      newSong.setTitle(previousSong.getTitle());
      newSong.setComposer(previousSong.getComposer());
      newSong.setDifficulty(previousSong.getDifficulty());

      const newControler = new SongControler(newSong, studio.getTransport(), studio.getAudioContext());
      await newControler.load();
      // La séquence démonte le morceau qu'elle remplace — contrôleur, lanes,
      // ordonnanceurs — sans quoi son audio continuerait sous la version
      // transposée.
      studio.getSequence().replace(newControler);

      studio.setTonality(target);
      // `sourceTonality` se recalcule depuis le morceau *courant*, donc
      // `semis` est relatif à ce qui est affiché, pas au fichier importé.
      // On cumule pour garder l'écart absolu, seul à identifier la variante.
      setTransposeSemis((previous) => previous + semis);
    },
    [studio, sourceMidiId, sourceTonality, songControler],
  );

  const songAnalysis = useMemo(
    () => (songControler ? songControler.getSong().getAnalysis() : null),
    [songControler],
  );

  const sections = useMemo<AnalysisSection[]>(
    () => (songControler ? songControler.getSong().getSections() : []),
    [songControler],
  );


  // Where the piece departs from its key and returns to it, for the roll's
  // tonality margin. Empty while the piece stays in its key, and cleared with
  // the piece.
  useEffect(() => {
    if (!studio) {
      return;
    }

    studio.setKeySpans(songAnalysis ? keySpansOf(songAnalysis) : []);
  }, [studio, songAnalysis]);

  const phrases = useMemo(
    () => (songControler ? songControler.getSong().getPhrases() : null),
    [songControler],
  );

  const phraseItems = useMemo(() => phrases?.items ?? [], [phrases]);

  /**
   * Traduit la boucle du navigateur (des mesures) en plage de ticks pour le
   * transport.
   *
   * `endTick` est le **début de la mesure suivante**, pas celui de la
   * dernière mesure : boucler « 5 à 8 » doit jouer la mesure 8 en entier.
   * Sur la dernière mesure du morceau, il n'y a pas de suivante et on prend
   * la durée totale.
   */
  const handleLoopChange = useCallback(
    (next: LoopRange | null, activate?: boolean) => {
      setLoop(next);

      // `activate` non fourni = on garde l'état courant : re-sélectionner une
      // plage pendant qu'on boucle ne doit pas couper le bouclage.
      const nextLooping = next !== null && (activate ?? looping);
      setLooping(nextLooping);

      // Une boucle armée, c'est une plage qui se met à tourner : le bouclage
      // qui s'allume, ou la plage qui change pendant qu'il tourne. Repasser la
      // **même** plage n'en est pas une, et c'est exactement ce que fait le
      // bouton « Boucler » en la relayant ici — sans cette comparaison, il
      // compterait une boucle de plus à chaque bascule.
      const rangeChanged =
        next !== null &&
        (loop === null ||
          loop.startMeasure !== next.startMeasure ||
          loop.endMeasure !== next.endMeasure);

      if (nextLooping && (!looping || rangeChanged)) {
        recordLoopArmed();
      }

      if (!studio) {
        return;
      }

      if (next === null || !songControler) {
        studio.setLoop(null);
        studio.setWorkRange(null);

        return;
      }

      const song = songControler.getSong();
      const durationTicks = song.getDurationTicks();
      const bars = song.getMusicalTimeline().getBars(durationTicks);
      const first = bars[next.startMeasure - 1];

      if (!first) {
        studio.setLoop(null);
        studio.setWorkRange(null);

        return;
      }

      const afterLast = bars[next.endMeasure];
      const range = {
        startTick: first.startTick,
        endTick: afterLast ? afterLast.startTick : durationTicks,
      };

      // Le rouleau assombrit l'extérieur de la plage et règle ses bords —
      // seulement quand la boucle tourne. Sélectionner des mesures n'est
      // qu'un repérage dans le panneau : assombrir le reste de la partition
      // pour un clic annonce « on ne joue plus que ça » alors que la lecture,
      // elle, continue tout droit.
      // `setWorkRange` était prévu pour le passage travaillé du parcours
      // d'apprentissage, qui ne l'appelle pas encore : les deux se
      // disputeraient ce créneau unique le jour où il le fera.
      studio.setWorkRange(nextLooping ? range : null);
      studio.setLoop(nextLooping ? range : null);
    },
    // `loop` n'entre ici que pour la mesure : c'est la plage d'avant, et il
    // faut la comparer à celle qui arrive pour savoir si la boucle change
    // vraiment. Le rappel change alors d'identité à chaque sélection, ce que
    // le panneau supporte — il se re-rend déjà sur la même donnée.
    [studio, songControler, loop, looping],
  );

  /** Le bouton « Boucler » de la barre de pied : arme ou retire le bouclage. */
  const handleToggleLooping = useCallback(() => {
    if (loop === null) {
      return;
    }

    handleLoopChange(loop, !looping);
  }, [loop, looping, handleLoopChange]);

  const handleSeekToMeasure = useCallback(
    (measure: number) => {
      if (!studio || !songControler) {
        return;
      }

      const song = songControler.getSong();
      const timeline = song.getMusicalTimeline();
      const bars = timeline.getBars(song.getDurationTicks());
      const bar = bars[measure - 1];

      if (bar) {
        studio.animateScrollToTick(bar.startTick);
      }
    },
    [studio, songControler],
  );

  /**
   * Oublier le morceau gardé pour le « retour », s'il y en a un : un autre
   * chargement vient de rendre ce retour impossible.
   */
  const dropPreviousSong = useCallback(() => {
    if (previousSongControler === null) {
      return;
    }

    studio?.getSequence().discard(previousSongControler);
    setPreviousSongControler(null);
  }, [studio, previousSongControler]);

  const handleGeneratedSongLoaded = useCallback(
    (newControler: SongControler) => {
      if (!studio) {
        return;
      }

      // Le morceau qu'on quitte reste en vie pour le retour, hors scène : il
      // ne sonne plus sous l'exercice, ce qu'il faisait quand le panneau de
      // génération empilait sa piste sur la sienne. Celui qu'on gardait
      // d'avant, lui, ne reviendra plus.
      if (songControler) {
        dropPreviousSong();
        studio.getSequence().withdraw(songControler);
        setPreviousSongControler(songControler);
      }

      // Le panneau de génération et la modale d'import montent leur piste
      // eux-mêmes avant de prévenir : la séquence la reprend telle quelle au
      // lieu d'en monter une seconde.
      studio.getSequence().replace(newControler);
      setIsGenerated(true);
      // Le panneau de génération passe aussi par ici, et ce qu'il produit
      // n'a pas d'identifiant d'exercice : sans cet oubli, la session
      // porterait celui de l'exercice précédent.
      setLoadedExerciseId(null);
    },
    [studio, songControler, dropPreviousSong],
  );

  const handleImportedSongLoaded = useCallback(
    (
      newControler: SongControler,
      midiFileId: string | null,
      hints?: QualityFlag[],
    ) => {
      // La séquence démonte le morceau qu'elle remplace ; sans ça l'ancienne
      // piste continuait de vivre dans le studio — audio ordonnancé, lanes
      // dessinées — et s'empilait sur la nouvelle.
      studio?.getSequence().replace(newControler);
      dropPreviousSong();
      setIsGenerated(false);
      setLoadedExerciseId(null);
      setSourceMidiId(midiFileId);
      setSourcePieceId(null);
      setTransposeSemis(0);
      // Le PieceOverviewOverlay recouvre tout le canvas dès qu'un morceau est
      // chargé, on met donc le transport en pause pour que le piano-roll
      // n'avance pas en arrière-plan pendant que l'overlay est affiché.
      studio?.getLocalTransport().detach();
      setPieceOverviewOpen(true);
      // Un morceau qui arrive se présente ; il ne demande pas tout de suite par
      // où on veut le prendre.
      setPieceOverviewPhase("piece");

      // `ok_with_hints` flow (spec §3.1) : the modal forwards the active
      // flags so the studio can surface a non-blocking toast. Skip when no
      // recognized flag yields a wording, the resolver returns null.
      if (!hints || hints.length === 0) {
        setOkWithHintsToastMessage(null);

        return;
      }

      const message = resolveOkWithHintsToastMessage(hints);

      if (message === null) {
        setOkWithHintsToastMessage(null);

        return;
      }

      setOkWithHintsToastMessage(message);
      trackOkWithHintsToastShown({
        fileId: midiFileId ?? undefined,
        hints,
        resolvedMessage: message,
      });
    },
    [studio, dropPreviousSong],
  );

  const handleOkWithHintsToastClose = useCallback(() => {
    setOkWithHintsToastMessage(null);
  }, []);

  const handleBack = useCallback(() => {
    if (!previousSongControler || !studio) {
      return;
    }

    // Le morceau gardé remonte en scène, tel qu'on l'a quitté, et l'exercice
    // qu'il remplace est démonté avec lui.
    studio.getSequence().replace(previousSongControler);
    setPreviousSongControler(null);
    setIsGenerated(false);
  }, [previousSongControler, studio]);

  const handleUploadOpen = useCallback(() => {
    entryFunnel.doorChosen("upload");
    setPendingUploadFile(null);
    setUploadOpen(true);
  }, [entryFunnel]);
  const handlePracticeOpen = useCallback(() => setPracticeOpen(true), []);
  /*
    Chaque fermeture ne referme que la sienne : le panneau qui se ferme peut
    très bien être celui qu'on vient de remplacer — sa transition de sortie
    joue encore, et son `onClose` refermerait alors celui qui vient d'ouvrir.
  */
  const handleGenerateClose = useCallback(
    () => setRightPanel((prev) => (prev === "generate" ? null : prev)),
    [],
  );

  const handleLivePanelClose = useCallback(
    () => setRightPanel((prev) => (prev === "live" ? null : prev)),
    [],
  );

  const handleToggleLivePanel = useCallback((open: boolean) => {
    setRightPanel((prev) => {
      if (open) {
        return "live";
      }

      return prev === "live" ? null : prev;
    });
  }, []);

  /** Le voile du bandeau, en mode compact : il ferme ce qui est ouvert. */
  const handleCloseRightPanel = useCallback(() => setRightPanel(null), []);

  // La bibliothèque est une route depuis la refonte : on y navigue au lieu de
  // lever un booléen global. `StudioPage` reste monté (le layout ne le démonte
  // jamais), donc partir d'ici ne recharge ni le piano-roll ni les samples.
  const handleEmptyOverlayLibrary = useCallback(() => {
    entryFunnel.doorChosen("library");
    router.push(LIBRARY_BASE_PATH);
  }, [router, entryFunnel]);

  const handleEmptyOverlayPlay = useCallback(() => {
    if (!studio) {
      return;
    }

    entryFunnel.doorChosen("free_play");
    cancelGrace();
    setEmptyOverlayVisible(false);

    // Free-play start, no count-in, no section tip. The user wants to
    // hit keys immediately and have them captured by the piano-roll.
    const transport = studio.getLocalTransport();

    if (transport.isDetached) {
      transport.play();
    }

    scheduleOverlayReappear();
  }, [studio, cancelGrace, scheduleOverlayReappear, entryFunnel]);

  const handleEmptyOverlayRecall = useCallback(() => {
    entryFunnel.recalled();
    cancelGrace();
    cancelOverlayReappear();
    resetActivityCounters();
    setEmptyOverlayVisible(true);
  }, [cancelGrace, cancelOverlayReappear, resetActivityCounters, entryFunnel]);

  const handleFileDropped = useCallback(
    (file: File, _kind: DroppedFileKind) => {
      // Un fichier lâché sur l'écran vide passe par la même modale que
      // « Charge un morceau » : c'est la même porte, entrée par l'autre côté.
      // Sur un morceau déjà chargé, l'entonnoir n'est pas exposé et le geste
      // n'est donc pas compté.
      entryFunnel.doorChosen("upload");

      // The UploadModal re-classifies the file itself, kind is there for the
      // visual layer, not the upload pipeline.
      setPendingUploadFile(file);
      setUploadOpen(true);
    },
    [entryFunnel],
  );

  const handleUnsupportedFile = useCallback((file: File) => {
    if (errorTimerRef.current !== null) {
      clearTimeout(errorTimerRef.current);
    }

    // A MuseScore project is refused like the rest, but it is the one refusal
    // with a next step to give.
    setErrorKind(isMuseScoreFile(file) ? "musescore" : "unknown");
    setErrorActive(true);

    errorTimerRef.current = setTimeout(() => {
      setErrorActive(false);
      errorTimerRef.current = null;
    }, 2500);
  }, []);

  const handleUploadClose = useCallback(() => {
    setUploadOpen(false);
    setPendingUploadFile(null);
  }, []);

  const handleCloseSong = useCallback(() => {
    if (!studio || !songControler) {
      return;
    }

    studio.getSequence().clear();

    setLoop(null);
    setLooping(false);
    studio.setLoop(null);
    studio.setWorkRange(null);

    dropPreviousSong();
    setIsGenerated(false);
    setLoadedExerciseId(null);
    setSourceMidiId(null);
    setSourcePieceId(null);
    setTransposeSemis(0);
    studio.setTonality(null);
  }, [studio, songControler, dropPreviousSong]);

  const loadExerciseAsSong = useCallback(
    async (exercise: ExerciseDetail) => {
      if (!studio) {
        return;
      }

      const song = createSongFromRawAnalysis(exercise.analysisData);
      const newControler = new SongControler(song, studio.getTransport(), studio.getAudioContext());
      await newControler.load();

      // C'est la séquence qui met de côté l'exercice ou le morceau d'avant et
      // monte celui-ci ; sans ça les deux s'empilaient sur le piano-roll,
      // audio compris.
      handleGeneratedSongLoaded(newControler);

      // Point de passage unique de tous les démarrages d'exercice : la route
      // `/learn/…` y arrive via `useStudio().loadExercise`, et le studio
      // l'appelle directement pour les exercices lancés depuis l'app.
      startExerciseSession(exercise.id, newControler, {
        difficulty: exercise.difficulty,
        category: exercise.category,
      });
      // Après `handleGeneratedSongLoaded`, qui l'efface : c'est ce chemin-ci,
      // et lui seul, qui sait de quel exercice il s'agit.
      setLoadedExerciseId(exercise.id);
      setPendingExerciseStart(true);
    },
    [studio, handleGeneratedSongLoaded],
  );

  /**
   * L'exercice qu'on vient de lancer part tout seul, du début.
   *
   * Le démarrage ne peut pas avoir lieu dans `loadExerciseAsSong` : la
   * pré-séance charge encore depuis `/learn`, le canvas y est masqué, et le
   * décompte s'y jouerait pour personne. On attend donc deux choses — être
   * arrivé sur le studio, et que le roll soit la vue montée, parce que c'est
   * lui qui monte l'interstitiel de section. Sans cette seconde condition,
   * `startWithCountIn` annoncerait un conseil que rien n'affiche, et la
   * promesse qu'il attend ne serait jamais tenue : ni interstitiel, ni
   * décompte, ni lecture.
   *
   * `startWithCountIn(0)` fait le reste dans l'ordre : conseil de section
   * s'il y en a un, décompte, lecture. Le retour au début est le sien — le
   * décompte cale le transport sur la mesure visée avant de sonner.
   */
  useEffect(() => {
    if (!studio || !pendingExerciseStart || !visible || studioView !== "roll") {
      return;
    }

    setPendingExerciseStart(false);
    // Le transport tourne peut-être encore : en jeu libre, la première touche
    // frappée le lance, et il n'a aucune raison de s'être arrêté avant qu'on
    // parte vers `/learn`. L'arrêter d'abord, sinon la playhead continue
    // d'avancer pendant que la vue défile vers le début.
    studio.getLocalTransport().detach();
    // Le métronome s'allume à la sortie du décompte, pas avant : `startWithCountIn`
    // ne rend la main qu'après avoir lancé la lecture, donc le clic part avec le
    // piano-roll. L'allumer plus tôt aurait fait sonner la pulsation libre — celle
    // qui tourne transport à l'arrêt — pendant le défilement vers le début, puis
    // par-dessus le conseil de section.
    void studio.startWithCountIn(0).then(() => {
      studio.setMetronomeEnabled(true);
    });
  }, [studio, pendingExerciseStart, visible, studioView]);

  // Un exercice cesse d'être actif dès que le morceau chargé n'est plus le
  // sien — import d'un fichier, chargement d'une pièce du catalogue, retour
  // au studio vide. Observer `songControler` couvre ces cinq chemins d'un
  // coup, là où câbler chaque appelant en oublierait un tôt ou tard.
  useEffect(() => {
    endExerciseSessionUnlessOwnedBy(songControler);
  }, [songControler]);

  // Register loadExercise so other pages (e.g. learn) can load exercises into the piano roll
  useEffect(() => {
    registerLoadExercise(loadExerciseAsSong);
  }, [registerLoadExercise, loadExerciseAsSong]);

  // ── Le répertoire ────────────────────────────────────────────
  //
  // Les morceaux que le joueur tient déjà, celui dont le silence est écoulé,
  // et celui qu'il est en train de gravir. Lu à la seule surface dont c'est
  // le métier — l'écran vide —, jamais pendant un rendu : `Date.now()` et
  // `localStorage` sont derrière ces deux callbacks, appelés par l'overlay à
  // son montage.
  const offerRef = useRef<Offer | null>(null);

  // Pas de `exclude` : l'écran vide n'est monté que s'il n'y a aucun morceau
  // chargé, donc il n'y a jamais de morceau courant à exclure.
  //
  // L'arbitrage entre les deux offres est dans `offer()`, pas ici : cette
  // page est la seule capable d'en afficher deux à la fois, donc la seule à
  // qui il ne faut pas confier la règle du plafond d'une.
  const readResume = useCallback((): ResumeOffer | null => {
    const offer = repertoire().offer(Date.now());
    offerRef.current = offer;

    if (offer === null) {
      return null;
    }

    if (offer.kind === "replay") {
      return {
        kind: "replay",
        title: offer.entry.title,
        fact: actDuration(offer.entry.seconds),
      };
    }

    return {
      kind: "resume",
      title: offer.piece.title,
      // Le rang de la phrase se relit du document de parcours, il n'est pas
      // recopié dans le pointeur : c'est la seule des trois informations qui
      // bouge à chaque session, et deux copies d'un chiffre qui bouge sont
      // deux occasions d'en afficher un faux.
      fact: resumeGlanceLabel(offer.piece.morceauId),
    };
  }, []);

  // Le refus ne vide pas `offerRef` : la ligne reste à l'écran tant que
  // l'overlay est monté, donc elle doit rester cliquable. Décliner dit
  // seulement « ne le repropose pas à la prochaine ouverture », et ne
  // concerne que le répertoire — l'overlay ne l'appelle pas pour une reprise.
  const handleDeclineResume = useCallback(() => {
    const offer = offerRef.current;

    if (offer !== null && offer.kind === "replay") {
      repertoire().decline(offer.entry.morceauId);
    }
  }, []);

  /**
   * Le seul chemin qui installe réellement un morceau du catalogue dans le
   * runtime, donc le seul qui puisse dire qu'un morceau est **ouvert**.
   *
   * `piece_opened` était émis par les surfaces qui *lancent* l'ouverture, et
   * décrivait donc deux instants selon la provenance : une navigation depuis
   * une fiche, un chargement depuis l'import. Ici, l'événement dit toujours la
   * même chose, `source` redevient comparable d'une provenance à l'autre, et
   * `loadMs` mesure enfin la première attente subie du produit.
   *
   * `pieceId` est l'identité **catalogue** — celle qui sait rouvrir le morceau
   * — et jamais `midiFileId` : les deux se mélangeaient selon la source, ce qui
   * rendait le champ inventilable.
   *
   * Pas de `trackOnce` : ce chemin est déclenché par un geste et ne rejoue pas
   * de lui-même, et dédoublonner sur l'identité du morceau ferait disparaître
   * la seconde ouverture d'un morceau qu'on revient travailler.
   */
  const loadPieceFromCatalog = useCallback(
    async (
      pieceId: string,
      source?: PieceSource,
      scoreId?: string,
    ): Promise<{ midiFileId: string | null }> => {
      // Consommée dans tous les cas, même quand l'appelant sait : une annonce
      // laissée en place ressortirait à la prochaine ouverture du **même**
      // morceau, où elle décrirait un geste vieux d'une navigation entière.
      const announced = consumePieceSource(pieceId);

      const origin =
        // Ce que l'appelant **sait** passe en premier : la reprise passe
        // explicitement `"library"`, et c'est bien de là que part le geste,
        // même si un écran visité plus tôt avait laissé une annonce pour ce
        // morceau.
        source ??
        // L'annonce est le repli des appelants qui ne savent pas, et elle bat
        // la route : le morceau de récompense d'un parcours s'ouvre depuis sa
        // fiche de catalogue, et rien dans l'URL ne dit qu'on vient de /learn.
        announced ??
        // La route en dernier, lue maintenant : l'appelant navigue vers
        // `/studio` *après* le chargement, donc elle porte encore la surface
        // d'où part le geste. Même lecture que `exercise-session` pour
        // retrouver un programme.
        (typeof window === "undefined"
          ? "library"
          : pieceSourceFromPathname(window.location.pathname));

      if (!studio) {
        // L'échec le plus silencieux de tous : on rendait `null` et l'appelant
        // naviguait vers un studio vide sans que rien ne le dise.
        track({
          name: "piece_load_failed",
          properties: { source: origin, reason: "studio_not_ready", pieceId },
        });

        return { midiFileId: null };
      }

      const startedAt = performance.now();
      let fetched = false;

      try {
        const apiResponse = await loadPieceSong(pieceId, scoreId);
        fetched = true;
        const song = createSongFromAnalysis(apiResponse);

        setSourceAnalysis(apiResponse.score.content);
        const newControler = new SongControler(song, studio.getTransport(), studio.getAudioContext());
        await newControler.load();
        handleImportedSongLoaded(newControler, apiResponse.midiFileId);
        setSourcePieceId(pieceId);
        // Un autre morceau n'a pas de niveau ouvert : sans ça, le parcours du
        // nouveau tournerait sous la clé d'un niveau de l'ancien.
        setLoadedRung(null);

        const firstTick = Math.min(
          ...song.getParts().map((p) => p.getRawNotes()[0]?.tick ?? Infinity),
        );

        if (firstTick < Infinity) {
          studio.getLocalTransport().seekToTick(firstTick);
        }

        track({
          name: "piece_opened",
          properties: {
            source: origin,
            pieceId,
            loadMs: Math.round(performance.now() - startedAt),
          },
        });

        return { midiFileId: apiResponse.midiFileId };
      } catch (error) {
        // L'échec était rattrapé par l'appelant puis perdu : un morceau qui ne
        // s'ouvre pas ressemblait à quelqu'un qui change d'avis. On relance,
        // la mesure n'enlève rien à personne.
        track({
          name: "piece_load_failed",
          properties: {
            source: origin,
            reason: pieceLoadFailureReason(error, fetched),
            pieceId,
          },
        });

        throw error;
      }
    },
    [studio, handleImportedSongLoaded],
  );

  useEffect(() => {
    registerLoadPiece((pieceId, scoreId) =>
      loadPieceFromCatalog(pieceId, undefined, scoreId),
    );
  }, [registerLoadPiece, loadPieceFromCatalog]);

  /**
   * Remplace le morceau joué par une réduction de niveau.
   *
   * Même recette que la transposition juste au-dessus, et pour la même raison :
   * la piste précédente doit être démontée, sinon son audio continue sous la
   * nouvelle. Le titre suit, sans quoi le bandeau se vide à chaque changement
   * de niveau.
   */
  const applyLabAnalysis = useCallback(
    async (analysis: AnalysisResult) => {
      if (!studio) {
        return;
      }

      setLabBusy(true);

      try {
        const previous = songControler;
        const song = createSongFromRawAnalysis(analysis);

        if (previous) {
          const previousSong = previous.getSong();

          song.setTitle(previousSong.getTitle());
          song.setComposer(previousSong.getComposer());
        }

        const controler = new SongControler(song, studio.getTransport(), studio.getAudioContext());

        await controler.load();
        // La séquence démonte la piste précédente en montant celle-ci.
        studio.getSequence().replace(controler);
      } finally {
        setLabBusy(false);
      }
    },
    [studio, songControler],
  );

  /**
   * Ouvre le parcours harmonique sur le niveau qui vient d'être posé.
   *
   * Passe par `setStudioView` et donc par une navigation douce : taper la
   * route à la main remonterait la page, et le morceau, qui ne vit qu'en
   * mémoire, serait perdu avec elle. C'est le piège que le banc de l'échelle
   * documente déjà pour sa propre entrée de nav.
   */
  const handleOpenLevel = useCallback(
    (rung: HarmonyContext) => {
      // La tête de lecture revient au début.
    //
    // Depuis que l'écran du niveau suit le transport, l'unité montrée est celle
    // où la tête de lecture se trouve. « Travailler ce niveau » ouvrait donc le
    // niveau là où le roll traînait : quelqu'un qui avait fait glisser la
    // minimap avant de cliquer atterrissait à l'unité 8. Ce bouton est le geste
    // qui **commence** un niveau, et rien à l'intérieur de l'écran ne permet de
    // revenir à son début.
    //
      // Le rembobinage est ici et pas dans `HarmonyLevel` : reprendre là où
      // l'on en était reste ce qu'il faut faire quand l'écran se remonte pour
      // une autre raison, et seul ce bouton-ci veut dire « depuis le début ».
      studio?.getLocalTransport().seekToTick(0);

      // Et l'engagement se pose, ce qui monte l'écran du niveau. Un seul
      // geste : il remplace ce qui était là, donc rien à désarmer d'abord.
      // Le niveau voyage **dans** l'engagement, si bien que le parcours
      // harmonique ne peut pas exister sans lui.
      //
      // La fiche du morceau, qui recouvre le canvas, se referme d'elle-même :
      // c'est la règle unique qui veut qu'un parcours et la fiche ne partagent
      // pas l'écran, et elle vaut pour les deux parcours. Voir plus bas.
      studio?.setEngagement({ kind: "harmonique", rung });
    },
    [studio],
  );

  /**
   * Ouvrir le parcours harmonique sur le niveau qu'on vient de choisir.
   *
   * Trois gestes qui ne doivent jamais se séparer, et c'est pour ça qu'ils
   * vivent dans une seule fonction. **Poser les notes** du niveau sous les
   * mains, **le déclarer** au parcours, et **ouvrir** l'écran dessus. Des notes
   * posées sans être déclarées feraient tourner le parcours du morceau entier
   * sur celles d'un niveau, et un niveau déclaré sans ses notes ferait
   * l'inverse. Le hash du plan de phrases protège du pire, il repartirait de
   * zéro plutôt que de mentir, mais partir de zéro n'est pas une
   * fonctionnalité.
   *
   * L'ordre compte : l'ouverture vient en dernier, après que les notes sont
   * chargées, parce que le parcours découpe ses unités sur le morceau en
   * mémoire. L'ouvrir plus tôt le ferait démarrer sur celui d'avant.
   *
   * Ce geste vivait dans la barre latérale de l'échelle, qui n'existe plus :
   * le choix du niveau se fait maintenant dans le sas d'entrée, et il n'y a
   * plus d'écran entre « Apprendre » et le premier accord.
   */
  const handlePickRung = useCallback(
    async (index: number) => {
      if (!sourceAnalysis) {
        return;
      }

      setLabBusy(true);

      try {
        const level = await fetchRung(sourceAnalysis, "harmonique", index);

        const rung = {
          rungIndex: level.index,
          rungId: level.rungId,
          realisation: level.realisation,
          scaffolded: level.scaffolded,
          // L'harmonie du morceau, nommée comme ce niveau-ci la fait prendre.
          //
          // Le niveau ne change pas la grille — même fondamentale, même degré,
          // même place — mais les trois premiers barreaux n'en posent que la
          // triade, et un nom qui annonce une septième au-dessus de trois notes
          // qui ne la contiennent pas fait douter de tout l'écran. Voir
          // `withoutUnplayedExtension`.
          chords: withoutUnplayedExtension(sourceAnalysis.chords ?? [], level.index),
          // Les notes du morceau non plus : c'est le passage d'origine que
          // l'écran fait entendre, pas la réduction du niveau.
          pieceNotes: sourceAnalysis.notes,
          degreesTrusted: sourceAnalysis.key.degreesTrusted ?? false,
          news: level.news,
          ladder: LADDER_SHAPE.harmonique.map((id, at) => ({
            index: at,
            rungId: id,
            state: at < level.index ? "traversed" : at === level.index ? "here" : "ahead",
          })),
        } satisfies HarmonyContext;

        setLoadedRung(rung);
        await applyLabAnalysis(asAnalysis(sourceAnalysis, level));
        handleOpenLevel(rung);
      } finally {
        setLabBusy(false);
      }
    },
    [sourceAnalysis, applyLabAnalysis, handleOpenLevel],
  );

  /**
   * « Apprendre » retourne la fiche du morceau sur ses deux parcours.
   *
   * Le mot ne promettait qu'un chemin parce qu'il n'en existait qu'un le jour
   * où on l'a écrit ; il en ouvre deux, et c'est le joueur qui tranche.
   *
   * Rien ne se monte et rien ne se ferme : la fiche se réorganise sur place.
   * Elle a toujours été le point d'entrée de l'apprentissage, et lui superposer
   * un écran qui redisait le titre, le compositeur et la tonalité revenait à
   * répondre deux fois à la question qu'elle avait déjà posée.
   *
   * Aucun parcours n'est engagé non plus : lequel dépend du choix, et personne
   * n'a encore choisi. L'engagement se pose à la sortie, dans `handleOpenLevel`
   * pour le parcours harmonique et dans `handleLearnByHeart` pour l'autre.
   *
   * L'analyse n'est plus exigée. Elle l'était parce que l'écran d'entrée
   * montrait l'échelle des niveaux, qui n'existe pas sans elle ; le résultat
   * était qu'« Apprendre » ne faisait rien du tout sur un morceau non analysé,
   * en silence. Seul le parcours harmonique en dépend, et c'est à la fiche de
   * ne proposer que ce qu'elle peut tenir.
   */
  const handleEnterProgram = useCallback(() => {
    if (!songControler) {
      return;
    }

    setSettingsOpen(false);
    // Comme à l'ouverture d'un morceau : la fiche recouvre le canvas, donc le
    // roll n'a rien à jouer derrière elle.
    studio?.getLocalTransport().detach();

    // Et l'engagement se relâche, parce que ce geste s'atteint aussi depuis un
    // niveau du parcours harmonique, par la bascule de la barre du studio :
    // sans ça, l'écran du niveau resterait monté sous la fiche, avec son
    // décompte et sa machine, pendant qu'on choisit encore par où commencer.
    //
    // Ce qui est **chargé** ne bouge pas pour autant : la réduction du niveau
    // reste sous les mains, et `loadedRung` reste là pour le dire. C'est ce qui
    // permet à « Par cœur », deux clics plus loin, de rendre ses notes au
    // morceau.
    studio?.setEngagement(null);

    // La vue revient au roll, parce que la fiche se pose **par-dessus lui** et
    // non par-dessus n'importe quoi.
    setStudioView("roll");
    setPieceOverviewOpen(true);
    setPieceOverviewPhase("programme");
  }, [songControler, studio, setSettingsOpen, setStudioView]);

  /** Replier le choix : la fiche redevient la présentation du morceau. */
  const handleBackToPiece = useCallback(() => {
    setPieceOverviewPhase("piece");
  }, []);

  /**
   * Quitte le parcours harmonique pour celui par cœur, sur le vrai morceau.
   *
   * C'est la seconde porte de l'entrée du programme, et la seule du produit qui
   * mène d'un parcours à l'autre. Elle fait deux choses de plus que poser
   * l'engagement, et les deux sont obligatoires.
   *
   * **Elle rend ses notes au morceau.** Ce qui est chargé sous les mains est la
   * réduction du niveau, pas la partition : « Travailler ce niveau » les a
   * remplacées. Lancer le parcours par cœur dessus apprendrait par cœur un
   * modèle fabriqué, sous le titre et le compositeur du morceau, ce qui est
   * exactement le mensonge que le contrat des niveaux existe pour empêcher.
   * L'analyse d'origine est celle du catalogue, gardée intacte depuis le
   * chargement ; c'est déjà elle que l'échelle relit pour fabriquer ses niveaux.
   *
   * **Elle referme le niveau.** Sans quoi la clé de progression garderait son
   * suffixe `:n<rung>` et le parcours par cœur repartirait de zéro sous une
   * identité qui n'est pas celle du morceau, en écrasant au passage celle du
   * morceau lui-même. Voir `learnMorceauId`.
   *
   * Un morceau sans niveau ouvert n'a rien à rendre : `loadedRung` à `null` veut
   * dire que les notes chargées sont déjà celles de la partition, et l'analyse
   * n'est alors pas rejouée pour rien.
   *
   * Elle lit `loadedRung` et non l'engagement, et c'est la raison d'être des
   * deux. On arrive ici depuis la fiche, qui a relâché l'engagement en
   * s'ouvrant ; l'engagement dit donc « rien », alors que la réduction est
   * toujours chargée. Lire l'engagement rendrait ce test faux exactement sur le
   * chemin qu'il existe pour protéger.
   */
  const handleLearnByHeart = useCallback(async () => {
    if (!studio || !sourceMidiId) {
      return;
    }

    if (loadedRung !== null && sourceAnalysis !== null) {
      await applyLabAnalysis(sourceAnalysis);
    }

    setLoadedRung(null);

    // Pas de rembobinage, contrairement à « Travailler ce niveau ». Le parcours
    // par cœur sait où il en est et pose lui-même la tête de lecture sur la
    // phrase qu'il reprend ; la ramener à zéro juste avant lui reviendrait à
    // lui disputer sa propre reprise. C'est aussi ce que fait la reprise du
    // répertoire, l'autre porte du même parcours, et les deux ne doivent pas
    // diverger.
    setStudioView("roll");
    studio.setEngagement({ kind: "byHeart" });
  }, [studio, sourceMidiId, loadedRung, sourceAnalysis, applyLabAnalysis, setStudioView]);

  const handlePieceOverviewFreePlay = useCallback(() => {
    setPieceOverviewOpen(false);

    if (studio) {
      studio.setEngagement(null);
      setStudioView("roll");
    }
  }, [studio, setStudioView]);

  // Les deux offres ne mènent pas au même endroit, et c'est ce que leur verbe
  // annonce.
  //
  // « Rejouer X · ~2 min » : on va jouer, pas choisir. Passer par la fiche du
  // morceau et ses trois portes rendrait la promesse fausse d'un écran, donc
  // le mode parcours directement. On n'appelle pas `handleLearn` : sa closure
  // lit le `sourceMidiId` du rendu courant, donc celui d'avant le chargement
  // — encore `null`, et sa garde renvoyait le joueur en mode libre. C'est
  // l'identité que le chargement vient de rendre qui fait foi.
  //
  // « Reprendre X · Phrase 7 sur 19 » : la fiche, qu'ouvre déjà tout
  // chargement de morceau. Une reprise n'a rien promis sur ce qui va se
  // passer, et le joueur qui revient veut parfois relire le morceau avant de
  // remonter dessus. C'est aussi le seul écran qui sait quoi faire d'un
  // morceau qu'on ne peut pas rouvrir.
  const handleResume = useCallback(async () => {
    const offer = offerRef.current;

    if (offer === null || !studio) {
      return;
    }

    entryFunnel.doorChosen("resume");
    offerRef.current = null;

    if (offer.kind === "replay") {
      repertoire().decline(offer.entry.morceauId);
    }

    try {
      const pieceId =
        offer.kind === "replay" ? offer.entry.pieceId : offer.piece.pieceId;
      // La provenance est dite ici plutôt que devinée : le geste part du
      // studio vide, où la route ne dit rien de l'offre de reprise. Ce qu'on
      // rouvre est un morceau que la personne tient déjà.
      const { midiFileId } = await loadPieceFromCatalog(pieceId, "library");

      if (midiFileId === null) {
        return;
      }

      // `loadPieceFromCatalog` ouvre déjà la fiche : une reprise n'a plus
      // rien à faire ici, c'est l'écran demandé.
      if (offer.kind === "resume") {
        return;
      }

      setPieceOverviewOpen(false);
      setStudioView("roll");
      studio.setEngagement({ kind: "byHeart" });
    } catch {
      // Le réseau a lâché. On remet l'offre en place plutôt que de laisser
      // un clic mort : la ligne est toujours là, elle est toujours cliquable.
      offerRef.current = offer;
    }
  }, [studio, loadPieceFromCatalog, setStudioView, entryFunnel]);

  const handlePieceOverviewOpenHarmony = useCallback(() => {
    if (!studio) {
      return;
    }

    setPieceOverviewOpen(false);
    setHarmonyFocus({ kind: "overview" });
    setStudioView("harmony");
  }, [studio, setStudioView]);

  const handlePieceOverviewSeekToMeasure = useCallback(
    (measure: number) => {
      setPieceOverviewOpen(false);
      handleSeekToMeasure(measure);
    },
    [handleSeekToMeasure],
  );

  const handleShowPieceOverview = useCallback(() => {
    if (!songControler) {
      return;
    }

    setSettingsOpen(false);
    // Même logique que lors du chargement d'un morceau : l'overlay recouvre le
    // canvas, on met le transport en pause pour figer le piano-roll derrière.
    studio?.getLocalTransport().detach();
    setPieceOverviewOpen(true);
    // Ce geste-ci demande le morceau, pas le programme : c'est « la fiche du
    // morceau » de la barre latérale. La barre du studio a le sien, juste
    // au-dessus, qui demande le programme.
    setPieceOverviewPhase("piece");
  }, [songControler, studio, setSettingsOpen]);

  // Settings & piece-overview are mutually-exclusive overlays. Settings is
  // now driven from the AppHeader via studio-context, so the page can't
  // co-clear them in a single handler, instead we close the overview
  // reactively whenever settings open.
  useEffect(() => {
    if (settingsOpen) {
      setPieceOverviewOpen(false);
    }
  }, [settingsOpen]);

  // Un parcours et la fiche du morceau ne partagent pas le canvas.
  //
  // La fiche s'ouvre au chargement et seuls ses propres gestes la fermaient, si
  // bien que toute autre entrée dans un parcours laissait celui-ci tourner
  // *sous* elle : la fiche est un calque opaque qui couvre le canvas, et le
  // joueur lisait le titre, la tonalité et l'histogramme de difficulté au lieu
  // du roll que l'étape lui demandait de suivre. Pire, la
  // fiche garde un `keydown` sur `window` où `Escape` veut dire « quitter »,
  // donc arrêter une prise à temps quittait le parcours.
  //
  // Une règle ici plutôt qu'une ligne dans chaque porte d'entrée, et elle vaut
  // maintenant pour les deux parcours d'un coup : c'est le même axe.
  useEffect(() => {
    if (engagement !== null) {
      setPieceOverviewOpen(false);
    }
  }, [engagement]);

  const handleCloseSettings = useCallback(() => {
    setSettingsOpen(false);
  }, [setSettingsOpen]);

  // Slots passés à `<StudioFrame>` (sidebar / overlay / transient).
  // Décision en 2 niveaux, et l'ordre dit lequel prime :
  //  1. Un engagement recouvre le canvas. Les deux parcours rendent la sidebar
  //     par défaut et leur écran en transient, pas d'overlay studio.
  //  2. Sinon, c'est `studioView` qui décide : `roll` = affichage normal avec
  //     transients (décompte, tips, briefing), les autres vues remplacent
  //     sidebar+overlay par leur propre rendu.
  //
  // Les deux parcours sont voisins dans le même `switch`, et c'est le point de
  // ce découpage : ce sont deux réponses à une seule question.
  const defaultSidebar = (
    <DefaultStudioSidebar
      songTitle={songInfo?.title ?? replay.title}
      songComposer={songInfo?.composer ?? null}
      onCloseSong={handleCloseSong}
      onShowOverview={songControler ? handleShowPieceOverview : undefined}
      songControler={songControler}
      sections={sections}
      runs={runs}
      phraseItems={phraseItems}
      loop={loop}
      onLoopChange={handleLoopChange}
      onSeekToMeasure={handleSeekToMeasure}
      isLooping={looping}
      onToggleLooping={handleToggleLooping}
    />
  );

  // Drop affordance is *available* (window listener + visual frame) only
  // in the roll view, hors de tout parcours, and never while an overlay
  // owns the screen (Settings, Upload modal). The Harmony and Chords
  // views run their own UI on the canvas, drop there would be
  // confusing, and the upload modal embeds its own internal drop
  // zone, so leaving the window listener armed would double-fire.
  const dropEnabled =
    studioView === "roll" &&
    !settingsOpen &&
    !uploadOpen &&
    engagement === null &&
    // Ni le cadre ni l'écouteur de fenêtre : un fichier lâché sur un récital
    // n'a nulle part où aller.
    !spectator;

  const slots: { sidebar: ReactNode; overlay: ReactNode; transient: ReactNode } = (() => {
    if (engagement !== null) {
      switch (engagement.kind) {
        // Les panneaux du parcours flottant au-dessus du piano-roll nu.
        // `pointer-events-none` laisse les clics tomber sur le roll ; les
        // panneaux réarment le pointeur sur leurs propres surfaces.
        case "byHeart":
          return {
            sidebar: defaultSidebar,
            overlay: null,
            transient:
              learnMorceauId && songControler ? (
                <div
                  className="pointer-events-none absolute left-0 top-0 z-20"
                  // Le parcours par cœur s'arrête au-dessus du clavier du roll :
                  // ses écrans montrent des notes qui tombent jusqu'aux touches,
                  // et le clavier est le bas de cette chute. Le couvrir couperait
                  // la trajectoire juste avant son arrivée.
                  //
                  // C'est une règle de **cet** écran-ci et non du studio :
                  // l'autre parcours prend, lui, toute la hauteur. Voir son cas
                  // juste en dessous.
                  style={{ bottom: pianoHeight, right: minimapWidth }}
                >
                  {/*
                    Deux identités, et il en faut deux. `songId` est la clé de
                    *progression* : elle porte la transposition et le niveau
                    ouvert, donc plusieurs clés pour un même morceau, et elle
                    sert à retrouver un avancement. `pieceId` est l'identité
                    *catalogue*, celle-là même qui part dans `piece_opened` et
                    dans `studio_session_ended` — la donner ici est ce qui
                    permet de joindre une ouverture, la séance qu'elle a
                    produite et ce que le parcours en a fait, par simple
                    égalité. Remplacer l'une par l'autre casserait l'un des
                    deux usages à tous les coups.
                  */}
                  <LearnPiecePage songId={learnMorceauId} pieceId={sourcePieceId} />
                </div>
              ) : null,
          };

        // Le parcours harmonique, sur le niveau posé sous les mains. Même
        // montage que son voisin, et pour la même raison : la scène pose son
        // propre fond opaque. Le passer par le slot `overlay` lui collerait le
        // voile de `StudioFrame` sous les pieds, par-dessus un fond qui est
        // déjà le sien.
        case "harmonique":
          return {
            sidebar: defaultSidebar,
            overlay: null,
            transient: (
              <div
                className="pointer-events-none absolute left-0 top-0 z-20"
                // Toute la hauteur, clavier du roll compris, et c'est le seul
                // écran du studio à qui elle est donnée.
                //
                // Le studio ne borne plus cet écran-là : c'est la scène qui
                // décide, et elle prend tout — le clavier du roll compris, avec
                // une réglette d'octaves à sa place. Voir `HarmonyScene`.
                //
                // Ce n'est pas la reprise d'une idée déjà essayée. Une version
                // antérieure avait pris l'écran entier pour y dessiner un
                // clavier « à l'échelle réelle » que la géométrie ne pouvait
                // pas rendre : on remplaçait l'instrument par une imitation.
                // Ici on ne le remplace pas, on garde le seul fait qu'il
                // donnait — où sont les mains — et on rend la place au rail,
                // qui est devenu l'outil de lecture de cet écran.
                //
                // Ce que ça coûte, et qui est assumé : là où la scène couvre
                // le clavier, ses touches ne se jouent plus au doigt ni à la
                // souris, puisqu'elle capte le pointeur. Le clavier
                // d'ordinateur, le MIDI et le micro passent, eux, par la
                // fenêtre et non par le dessin, et ne perdent rien.
                style={{ bottom: 0, right: minimapWidth }}
              >
                <HarmonyLevel
                  rung={engagement.rung}
                  song={songControler?.getSong() ?? null}
                  // La même identité catalogue que `LearnPiecePage` reçoit, et
                  // pour la même raison : les sessions du parcours harmonique
                  // n'en portaient aucune, donc rien ne les joignait à
                  // `piece_opened` ni aux sessions du parcours par cœur.
                  pieceId={sourcePieceId}
                />
              </div>
            ),
          };
      }
    }

    switch (studioView) {
      case "harmony":
        return {
          sidebar: (
            <HarmonySidebar focus={harmonyFocus} onFocusChange={setHarmonyFocus} />
          ),
          overlay: (
            <HarmonyOverlay focus={harmonyFocus} onFocusChange={setHarmonyFocus} />
          ),
          transient: null,
        };

      case "lab":
        return {
          sidebar: (
            <LevelLabSidebar
              analysis={sourceAnalysis}
              onApply={async (next) => {
                // Le labo ne travaille pas un niveau de l'échelle : ce qu'il
                // pose reprend l'identité du morceau, sinon le parcours
                // continuerait sous la clé d'un niveau qui n'est plus là.
                setLoadedRung(null);
                await applyLabAnalysis(next);
              }}
              busy={labBusy}
            />
          ),
          overlay: null,
          transient: null,
        };

      case "chords":
        return {
          sidebar: (
            <ChordsSidebar
              songTitle={songInfo?.title ?? null}
              songComposer={songInfo?.composer ?? null}
              onCloseSong={handleCloseSong}
            />
          ),
          overlay: <ChordsOverlay />,
          transient: null,
        };

      // Vue "roll", affichage standard + transients (count-in, tips).
      default:
        return {
          sidebar: defaultSidebar,
          overlay: null,
          transient: (
            <>
              <StudioCountIn />
              <StudioSectionTips sections={sections} />
              {dropEnabled && (
                <StudioDropOverlay
                  dragKind={dragKind}
                  errorActive={errorActive}
                  errorKind={errorKind}
                />
              )}
              {/* L'accueil et son bouton de rappel proposent de charger,
                  d'ouvrir la bibliothèque ou de jouer : rien de tout ça
                  n'est pour le spectateur, qui a déjà quelque chose à
                  regarder. */}
              {!songControler && engagement === null && bootPhase === "ready" && !spectator && (
                <>
                  <EmptyStudioOverlay
                    visible={emptyOverlayVisible && dragKind === null && !errorActive}
                    dismissed={!emptyOverlayVisible}
                    onLoadMidi={handleUploadOpen}
                    onOpenLibrary={handleEmptyOverlayLibrary}
                    onPlay={handleEmptyOverlayPlay}
                    readResume={readResume}
                    onResume={handleResume}
                    onDeclineResume={handleDeclineResume}
                  />
                  {!emptyOverlayVisible && (
                    <EmptyOverlayRecallButton
                      onRecall={handleEmptyOverlayRecall}
                    />
                  )}
                </>
              )}
            </>
          ),
        };
    }
  })();

  // Settings overrides the standard (clamped) overlay slot regardless
  // of the current engagement. PieceOverview goes through the
  // full-canvas slot below, it's the only overlay allowed to cover
  // the piano keys and minimap. Settings still wins when both are
  // open (defensive, since the open handlers already coordinate).
  const overlay = settingsOpen ? (
    <SettingsOverlay onClose={handleCloseSettings} />
  ) : (
    slots.overlay
  );

  // Full-canvas overlay, only PieceOverview uses it. Renders only
  // while a song is loaded; guards against a stale `pieceOverviewOpen`
  // after teardown. Hidden when Settings is open so the standard
  // overlay (clamped) takes precedence.
  const fullCanvasOverlay =
    pieceOverviewOpen && songControler && !settingsOpen ? (
      <PieceOverviewOverlay
        song={songControler.getSong()}
        phase={pieceOverviewPhase}
        onEnterProgram={handleEnterProgram}
        onBackToPiece={handleBackToPiece}
        onFreePlay={handlePieceOverviewFreePlay}
        onOpenHarmony={handlePieceOverviewOpenHarmony}
        onSeekToMeasure={handlePieceOverviewSeekToMeasure}
        // Le parcours harmonique n'existe que si le morceau est analysé :
        // l'échelle fabrique ses niveaux à partir de l'analyse. Sans elle, la
        // fiche n'ouvre que l'autre parcours, au lieu d'offrir une porte qui
        // ne s'ouvrirait pas.
        rungIds={sourceAnalysis ? LADDER_SHAPE.harmonique : null}
        busy={labBusy}
        onPickRung={handlePickRung}
        onByHeart={handleLearnByHeart}
      />
    ) : null;

  /*
    Ce que l'écran monté cache du canvas, et que le studio n'a donc plus à
    dessiner.

    Déclaré ici parce que c'est ici que la géométrie est écrite. Les deux slots
    de `StudioFrame` posent `bg-overlay-deep`, qui est opaque : le slot standard
    s'arrête au-dessus des touches et à gauche de la minimap, donc il ne cache
    que le rouleau ; le slot plein canvas prend tout, minimap comprise, et il
    est le seul.

    Le parcours harmonique est un transient, donc il se pose lui-même : il prend
    toute la hauteur, clavier compris, et laisse la minimap — voir son cas dans
    le `switch` ci-dessus, où `bottom: 0, right: minimapWidth` dit exactement
    cela. Le parcours par cœur, lui, ne déclare rien : ses panneaux flottent
    au-dessus du rouleau nu, qu'on doit continuer de voir.

    Ce qui n'est pas déclaré est simplement dessiné, ce qui est le bon sens du
    doute : on perd du travail inutile, jamais une image qu'on attendait.
  */
  useStudioCanvasCover(
    fullCanvasOverlay !== null
      ? ALL_SURFACES
      : engagement?.kind === "harmonique"
        ? HARMONY_COVERS
        : overlay !== null
          ? ROLL_COVERS
          : NO_COVERS,
  );

  return (
    <div
      className={`flex-1 flex flex-col min-h-0 ${visible ? "" : behind ? "absolute inset-0" : "hidden"}`}
      inert={behind}
    >
      <StudioPianoHintBridge />

      {(demoWidgets.includes("video") || demoWidgets.includes("video-live")) && (
        <VideoWidgetDemo live={demoWidgets.includes("video-live")} />
      )}

      {(demoWidgets.includes("chat") || demoWidgets.includes("chat-mod")) && (
        <ChatWidgetDemo moderating={demoWidgets.includes("chat-mod")} />
      )}

      {demoWidgets.includes("hearts") && <ReactionWidgetDemo />}

      {/* La caméra du récital, dès qu'on est dans la salle : elle se tait d'elle-même sans vidéo. */}
      {onAir && <RecitalCamera />}

      {/* La console du récital, posée sur le rouleau plutôt que par-dessus :
          elle se tait chez un spectateur et hors récital. */}
      {onAir && <RecitalWidgets />}

      {/* La séance où l'on joue à plusieurs : son coin, ses statistiques et son
          chat. Elle se tait dans un récital. */}
      {onAir && <JamWidgets />}

      {/* « Seul ou à plusieurs », ouvert par le bouton de la barre : il se tait
          tant qu'on ne l'a pas ouvert, et en séance. */}
      <LobbyWidget />

      {/* Les prises de son propre enregistrement : les écouter, en publier une,
          jeter les autres. Elle se tait chez qui n'a pas joué ce récital, et
          prend le coin que la console du récital laisse libre — les deux ne
          sont jamais là ensemble, un replay n'est pas un direct. */}
      {replayId !== null && <ReplayTakesWidget />}

      {/* Toolbar (V3, doc bar, hidden when no piece) */}
      <Toolbar
        songInfo={songInfo}
        sourceTonality={sourceTonality}
        onTranspose={handleTranspose}
        isGenerated={isGenerated}
        onBack={previousSongControler ? handleBack : undefined}
        onOpenSettings={handleOpenSettings}
        onEnterProgram={songControler ? handleEnterProgram : undefined}
        sidebarOpen={sidebarShown}
        onToggleSidebar={toggleSidebar}
        livePanelOpen={livePanelOpen}
        onToggleLivePanel={handleToggleLivePanel}
        spectator={spectator}
      />

      {/*
        `ChordsProvider` est monté en permanence pour que `<StudioFrame>`
        (et donc `<PianoViewport2>`) garde la même position dans l'arbre
        React quel que soit le mode. Sinon, changer de mode ferait remonter
        `PianoViewport2`, qui rappellerait `studio.addPianoTrack(...)` alors
        que l'ancien track est encore en cours de teardown async →
        "Piano track already exists".

        Le montage permanent ne coûte rien ici : pas de polling, juste un peu
        de state local + lecture de `?practice=`. Ses consommateurs
        (ChordsSidebar / ChordsOverlay) ne sont rendus que dans la branche
        `chords` du switch ci-dessus.
      */}
      <ChordsProvider>
        <StudioFrame
          sidebar={slots.sidebar}
          sidebarOpen={sidebarShown}
          panelsCompact={panelsCompact}
          onCloseSidebar={closeSidebar}
          studio={<PianoViewport2 bootCanvas={canvasBooted} />}
          overlay={overlay}
          fullCanvasOverlay={fullCanvasOverlay}
          transient={slots.transient}
          rightPanelOpen={rightPanel !== null}
          onCloseRightPanel={handleCloseRightPanel}
          rightPanel={
            /*
              Les deux panneaux sont montés en permanence et repliés à zéro :
              c'est ce qui laisse la génération garder le texte saisi quand on
              va regarder la séance, et la séance garder son défilement quand on
              revient. Une rangée flex parce qu'ils s'animent en largeur — l'un
              des deux vaut toujours zéro, donc la rangée mesure exactement le
              panneau ouvert.

              Sauf pendant les 200 ms d'un échange, où les deux sont à mi-course
              et la rangée dépasse la largeur d'un panneau. Elle est ancrée à
              droite : sans le plafond et la coupe, ce dépassement sortirait par
              la gauche sur un téléphone et y ferait apparaître une barre de
              défilement horizontale le temps de l'animation. Le panneau qui
              sort se fait rogner, ce qui est exactement ce qu'on veut voir.
            */
            <div className="h-full flex max-w-full overflow-hidden">
              {/* Replié devant un spectateur même s'il était ouvert en
                  entrant : ce qu'il produit est un morceau à charger. */}
              <GeneratePanel
                open={generateOpen && !spectator}
                onClose={handleGenerateClose}
                onSongLoaded={handleGeneratedSongLoaded}
                songKey={songAnalysis?.key}
                songStats={songAnalysis?.statistics}
                songTitle={songInfo?.title}
              />

              <LiveSessionPanel open={livePanelOpen} onClose={handleLivePanelClose} />

              {/* Le mot du replay pendant qu'il s'ouvre, ou s'il a échoué :
                  ses commandes, elles, sont dans la barre d'outils. */}
              <ReplayNotice />

              {/* Les caméras du récital enregistré, calées sur la tête de
                  lecture. */}
              {replay.recitalId !== null && <ReplayCamera />}
            </div>
          }
        />
      </ChordsProvider>

      {/* Practice modal */}
      <PracticeModal
        open={practiceOpen}
        onClose={() => setPracticeOpen(false)}
        onStartExercise={loadExerciseAsSong}
      />

      {/* Upload modal */}
      <UploadModal
        open={uploadOpen}
        onClose={handleUploadClose}
        onSongLoaded={handleImportedSongLoaded}
        initialFile={pendingUploadFile}
      />

      {/* Window-wide MIDI/MusicXML/PDF drop zone. Armed in the roll view (free
          mode) regardless of whether a piece is loaded, users
          can drop a new file anytime to swap pieces. Disabled while the
          Upload modal is open: the modal embeds its own drop zone, and
          leaving the window listener armed would fire twice (one drop,
          two uploads). The visible feedback (dashed frame, "Lâche pour
          importer" copy) lives in `StudioDropOverlay`. */}
      <WindowFileDropZone
        enabled={dropEnabled}
        onFileDropped={handleFileDropped}
        onUnsupportedFile={handleUnsupportedFile}
        onDragChange={setDragKind}
      />

      {/* Non-blocking toast for the `ok_with_hints` MIDI quality flow.
          Mounted at the top level so it always floats top-center (the
          atom is `position: fixed`), outside any overlay slot that could
          clip or hide it. */}
      <ToastBanner
        open={okWithHintsToastMessage !== null}
        message={okWithHintsToastMessage ?? ""}
        onClose={handleOkWithHintsToastClose}
      />

      {/* Top-most surface. The boot state machine
          (checking → welcome → jingle → ready) lives here; the card is
          presentation only. */}
      <WelcomeStudioCard
        bootPhase={bootPhase === "welcome" ? "welcome" : null}
        ready={jingleReady}
        onEnter={handleEnterStudio}
      />
    </div>
  );
}

/**
 * « Phrase 7 sur 19 » pour le morceau en cours, ou `null`.
 *
 * Le même libellé que « Mes morceaux », lu du même document, par la même
 * fonction : deux formulations du même rang seraient deux occasions de
 * diverger. `null` quand le stockage est refusé ou que le document n'est pas
 * lisible par ce bundle, et la ligne se contente alors du titre — elle
 * n'invente pas un chiffre pour remplir sa place.
 */
function resumeGlanceLabel(morceauId: string): string | null {
  let storage: Storage;

  try {
    storage = window.localStorage;
  } catch {
    return null;
  }

  const glance = readJourneyGlance(storage, morceauId);

  return glance === null ? null : journeyGlanceLabel(glance);
}
